/**
 * 工程表の紙（工程管理の5回目、#104、docs/39 §7、Figma F2）。
 *   schedulePaperModel(source, options)：行・日・バー・休み・中止・節目・人数を紙の形にそろえ、紙の割り付け（期間で分ける・行で分ける）まで決める
 *   renderSchedulePaper(model)：紙の HTML（1ページ＝.sp-page。mm で絶対配置）と枚数
 *   ダイアログの見本（apps/web）と PDF（apps/api の Chromium）が同じ関数を使う。Excel（apps/api の exceljs）も同じ model から作る。
 *   暦の計算（休み・中止・「休日も」・バーの区切り）は packages/contract の関数（画面と同じ）。DOM も Node の API も使わない
 *
 * 紙の決まり（docs/39 §7.2 の3・4）
 *   ・用紙 A3 横（既定）・A4 横・A4 縦、余白 10mm
 *   ・1日の幅は、期間が入るときは紙の幅いっぱい（F2。20mm まで）。入らないときは 5mm から 2.5mm まで縮め、それでも入らなければ期間で紙を分ける（どの紙にも左の表と日付の見出し）
 *   ・行が入らなければ行で紙を分ける（どの紙にも上の見出しと日付の見出し。見出しの行が紙の一番下に来るときは次の紙へ）
 *   ・目盛（#290、docs/102 §2.1・#291 §2.1.1）：自動（日 → 旬 → 月）／日／旬／月。旬・月は表を月の1日〜末日にし、日曜・祝日を塗らず、バーを休みで区切らず1本、
 *     会社の休みと中止の期間は帯と名前。バーは目盛に関わらず日の位置（1日の幅＝旬の幅 ÷ その旬の日数、月の幅 ÷ その月の日数）。
 *     目盛「日」の紙は #290 の前と1バイトも変わらない（test/golden-schedule）。Excel は日の列のまま（API が日で作る）
 *   ・紙の順は「下へ、それから右へ」（Excel の既定と同じ：同じ期間の行を全部出してから次の期間）
 */
import {
  SCHEDULE_EXPORT_MSG as X,
  SCHEDULE_JUN_LABEL,
  SCHEDULE_JUN_SHORT,
  SCHEDULE_PAPERS,
  WEEKDAY_JA,
  calendarForRow,
  crewByDate,
  dateRuns,
  dayNo,
  headingIndexOf,
  headingSummary,
  headingDoneText,
  barDonePct,
  stateMarkOf,
  taskStateOf,
  TASK_STATE_LABEL,
  workDaysBetween,
  isOffDay,
  dayNotesByRow,
  isSiteWorkDay,
  isRowStopDay,
  lanesOf,
  mdOf,
  overviewPeopleOf,
  overviewRowsOf,
  overviewSitesOf,
  scheduleSpanOf,
  weekdayOf,
  workCalendar,
  workRuns,
  ymdOfDayNo,
  type CompanyOffDay,
  type ScheduleInclude,
  type ScheduleLink,
  type ScheduleOverview,
  type SchedulePaperKey,
  type ScheduleRow,
  type ScheduleScale,
  type ScheduleRowDay,
  type ScheduleDayNote,
  type ScheduleStop,
  type WorkCalendar,
  type Labels,
  type Product,
  SCHEDULE_CSV_RAIN_COL,
  overviewCsvHead,
  scheduleCsvHead,
  scheduleFill,
  scheduleShowsRain,
} from "@genbaryoku/contract";
import { esc, yen } from "./fmt.js";

/* ============================================================================
 * 材料
 * ========================================================================== */

/**
 * 会社の言葉と製品（#142 段5）：紙の題・列の見出し・CSV の見出しの {schedule}・{task}・{siteShort}・{contractor} と、雨の印（営業力は出さない）。
 * API は loadLabels と会社の製品、画面は useLabels と Me の製品。省けば現場力の既定の言葉
 */
export type ScheduleVocab = { labels: Labels; product: Product };

/** 案件の工程表の材料（API は DB から、画面は開いている工程表から同じ形を作る） */
export type ScheduleGroupSource = {
  kind: "group";
  vocab?: ScheduleVocab;
  /** 工事名（案件名） */
  subject: string;
  /** 会社の言葉（工事名⇄件名・工期⇄納期） */
  labels: { subject: string; period: string };
  /** 宛名（{顧客}の正式名＋敬称。「株式会社マッシュ 御中」） */
  addressee: string;
  company: string;
  /** 担当（案件の担当者） */
  staff: string;
  /** 今日（作成日・今日の線・「進みは … の時点」） */
  today: string;
  schedule: {
    rows: ScheduleRow[];
    links: ScheduleLink[];
    offWeekdays: readonly number[];
    holidayOff: boolean;
    useCompanyOffDays: boolean;
    stops: readonly ScheduleStop[];
    /** {task}の日ごとの休み・作業（#293。無ければ無し） */
    rowDays?: readonly ScheduleRowDay[] | undefined;
    /** {siteShort}の作業日（#296。無ければ無し） */
    workDays?: readonly string[] | undefined;
    /** 日のメモ（#299。紙には出さない＝CSV・Excel の「データ」の「日のメモ」だけ。無ければ無し） */
    dayNotes?: readonly ScheduleDayNote[] | undefined;
  };
  holidays: readonly (string | { date: string })[];
  companyOffDays: readonly CompanyOffDay[];
  /** 会社の設定「進捗を％で入力」（#292。省けば％＝前と同じ紙。false なら列「状況」＝空／着手／✓・バーは 完了＝全部・着手＝半分） */
  progressPct?: boolean;
};
/** 全体の工程表の材料（GET /schedule/overview の答え＋画面の並べ方・探す・開いている現場） */
export type ScheduleOverviewSource = {
  kind: "overview";
  vocab?: ScheduleVocab;
  mode: "site" | "vendor";
  q: string;
  open: readonly string[];
  ov: ScheduleOverview;
  company: string;
  today: string;
  /** 会社の設定「進捗を％で入力」（#292。省けば ov.company.progressPct、それも無ければ％） */
  progressPct?: boolean;
};
export type SchedulePaperSource = ScheduleGroupSource | ScheduleOverviewSource;
/** scale：目盛（#290）。省けば日（#290 の前と同じ紙） */
export type SchedulePaperOptions = {
  paper: SchedulePaperKey;
  from: string;
  to: string;
  include: ScheduleInclude;
  scale?: ScheduleScale;
  /** 選んだ期間（旬・月の目盛で表を月の1日〜末日に広げたとき、全体の{schedule}の上の段の「期間」はこちら。#291 §2.1.1） */
  shown?: { from: string; to: string };
  /** 「{task}の列を広く」（#300）：1列目に WIDE_NAME_MM を足す（上限は W のまま）。省けば足さない（前と同じ紙） */
  wideName?: boolean;
};

/* ============================================================================
 * 形（model）
 * ========================================================================== */

export type SchedulePaperColumn = { key: string; label: string; sub?: string; w: number; align: "l" | "c" | "r" };
/** 稼働日の続き（期間の最初の日＝0 の番号。両端を含む）と、その中の済んだ日数（a から数える。端数あり） */
export type SchedulePaperRun = { a: number; b: number; done: number };
export type SchedulePaperBar = {
  id: string;
  runs: SchedulePaperRun[];
  stops: { i: number; mark: "☂" | "止" }[];
  lane: number;
  label: string;
  done: boolean;
  /** {task}名（#291：旬・月の目盛と概略ではバーの上に 7pt で出す） */
  name?: string;
  /** 随時（#291：点線のバー） */
  dashed?: boolean;
};
export type SchedulePaperRowKind = "heading" | "task" | "milestone" | "memo" | "site" | "section" | "person";
export type SchedulePaperRow = {
  id: string;
  kind: SchedulePaperRowKind;
  /** 左の表の文字（列の順）。span の行は cells[0] が左の表いっぱい */
  cells: string[];
  span: boolean;
  /** 見出しの説明（見出しの右に薄く） */
  note: string;
  indent: boolean;
  bars: SchedulePaperBar[];
  milestones: { i: number; done: boolean }[];
  /** 重なりの日（朱の淡い地と朱の枠） */
  overlap: number[];
  /** バーの右に添える文字（現場の「3日遅れ・重なり」） */
  after: { i: number; text: string } | null;
  lanes: number;
  /** 見出しに手で入れた期間（#291 ★3。日の番号 [a, b]）＝薄い枠 */
  plan?: { a: number; b: number } | null;
  /** 見出しの期間の日付（#291 の残り・#293。旬・月の目盛でバーの日付を入れるとき枠の両端に）＝中に{task}があれば中の範囲、無ければ枠。日付の文字 */
  planDates?: { from: string; to: string } | null;
  /** 概略の行（#291：見出しの行に中の{task}を名前つきで並べる） */
  sum?: boolean;
  /** 概略の行の節目の名前（milestones と同じ並び） */
  msNames?: string[];
  /** 行の高さ（mm） */
  h: number;
};
/** siteOff＝{siteShort}の休み（#293。見出しの字は「休」・地色は休みの日と同じ）。siteWork＝{siteShort}の作業日（#296。地色なし・曜日の字は平日と同じ色） */
export type SchedulePaperDay = { ymd: string; d: number; w: string; off: boolean; hol: boolean; siteOff?: boolean; siteWork?: boolean };
/** 1枚の紙：行 [a, b)・日 [a, b) */
export type SchedulePaperPage = { rows: [number, number]; days: [number, number] };
export type SchedulePaperLegendKey = "done" | "plan" | "off" | "companyOff" | "stop" | "milestone" | "today" | "overlap";
/** 紙に描く目盛（自動はここまでに日・旬・月のどれかに決まる。#291 で週は旬） */
export type SchedulePaperScale = "day" | "jun" | "month";
/** 目盛の1目（日・旬・月）：日の番号 [a, b) */
export type SchedulePaperUnit = { a: number; b: number };
/** 旬・月の目盛の帯（会社の休み・中止の期間）：日の番号 [a, b]（両端を含む）と名前 */
export type SchedulePaperBand = { kind: "companyOff" | "stop"; a: number; b: number; name: string };
export type SchedulePaperModel = {
  kind: "group" | "overview";
  paper: { key: SchedulePaperKey; w: number; h: number; label: string; landscape: boolean; excel: number };
  include: ScheduleInclude;
  title: string;
  addressee: string | null;
  /** 上の段の左（[名前, 値]。工事名・工期／期間） */
  meta: [string, string][];
  company: string;
  created: string;
  staff: string;
  cols: SchedulePaperColumn[];
  leftW: number;
  from: string;
  to: string;
  days: SchedulePaperDay[];
  /** 1日の幅（mm）。日の目盛はどの日も同じ。旬・月の目盛は dayW（日ごと）で描く */
  dayMm: number;
  /** 目盛（#290） */
  scale: SchedulePaperScale;
  /** 日ごとの幅（mm。日の目盛はどれも dayMm） */
  dayW: number[];
  /** 目盛の目（日・旬・月） */
  units: SchedulePaperUnit[];
  /** 旬・月の目盛で描く帯（会社の休み・中止の期間） */
  bands: SchedulePaperBand[];
  todayIdx: number | null;
  rows: SchedulePaperRow[];
  /** その日の人数（全体の工程表で人数を入れたとき。日ごと） */
  crew: number[] | null;
  legend: SchedulePaperLegendKey[];
  note: string;
  pages: SchedulePaperPage[];
  /** 人の列があるか（Excel・CSV でも同じ列にする） */
  hasCrew: boolean;
  /** 雨の印（凡例の中止の ☂）を出すか。営業力は「止」（#142 段5） */
  rain: boolean;
  /** Excel の1枚目の名前（会社の言葉の「工程表」） */
  sheetMain: string;
  /** 「{task}の列を広く」（#300）。Excel の1列目も広げる */
  wideName: boolean;
};

/** 紙の寸法（mm）。どの紙も同じ（docs/39 §7.2 の4：余白 10mm） */
export const SP_MM = {
  margin: 10,
  headGroup: 24,
  headOverview: 17,
  /** 日付の見出し（月・日・曜日の3段） */
  date: 12,
  dateRow: 4,
  row: 7,
  lane: 5.2,
  /** 概略の1段（#291）：上に{task}名 7pt（2.6mm）＋バー */
  sumLane: 6.2,
  crew: 6,
  foot: 11,
  bar: 3.2,
  /** 縮めるときの上限・下限（docs/39 §7.2 の4）。期間が入るときは紙の幅いっぱい（dayFill まで） */
  dayMax: 5,
  dayMin: 2.5,
  dayFill: 20,
  /** 旬・月の目盛の1目の幅（#290、docs/102 §2.1・#291 §2.1.1）：下限（これより狭ければ次の目盛か、期間で紙を分ける）と上限（旬は紙の幅いっぱい） */
  junMin: 6,
  monthMin: 12,
} as const;

/**
 * 左の表の列の幅（mm）。A4 縦は狭い組。名前の列（工程・担当・現場・業者）はここを上限にして、
 * 中身のいちばん長いものに合わせて縮める（fitNameColumns。10/1 射場さん「工程と担当の幅を取りすぎ」）
 */
const W = {
  wide: { name: 60, assignee: 34, start: 12, days: 10, progress: 11, crew: 8, amount: 22, site: 60, siteAsg: 40, person: 60, sites: 14, overlap: 16, note: 44 },
  narrow: { name: 40, assignee: 24, start: 12, days: 8.5, progress: 10, crew: 7, amount: 18, site: 43, siteAsg: 26, person: 40, sites: 12, overlap: 13, note: 32 },
} as const;
/**
 * 名前の列の下限（mm）：見出しの字（「工程」「担当」「現場・工程」）が入る幅。
 * 案件の{schedule}の1列目（{task}・{trade}）は 30mm（A4 縦は 24mm）＝短い名前の{schedule}でも見出しの行が読める（#300）。全体の{schedule}は 20mm のまま
 */
const NAME_MIN = { group: { wide: 30, narrow: 24 }, overview: 20, assignee: 12 } as const;
/** 「{task}の列を広く」（#300）で1列目に足す幅（mm） */
export const WIDE_NAME_MM = 15;

/**
 * 名前の列（1列目と担当）を中身に合わせる：文字の幅（7.5pt。太字は 1.08 倍、字下げ・「◇ 」も数える）＋左右の余白 2mm＋遊び 1mm。
 * 下限は NAME_MIN、上限は W の値（それより長い名前は …）。
 * 見出し・段の行（span＝左の表いっぱいに流す。太字 8pt）は、名前が左の表の列の合計に入らなければ、足りない分を1列目に足す（#300。上限は同じ）。
 * メモの行は数えない（長い文は … で切る）。wide（「{task}の列を広く」）は最後に1列目へ WIDE_NAME_MM（上限は同じ）
 */
function fitNameColumns(cols: SchedulePaperColumn[], rows: readonly SchedulePaperRow[], o: { nameMin: number; wide: boolean }): void {
  const first = cols[0];
  const cap = first?.w ?? 0;
  cols.forEach((c, k) => {
    if (c.key !== "name" && c.key !== "assignee") return;
    let need = textMm(c.label, 7.5) * 1.08;
    for (const r of rows) {
      if (r.span) continue;
      const t = r.cells[k] ?? "";
      if (!t) continue;
      const bold = k === 0 && (r.kind === "milestone" || r.kind === "site" || r.kind === "person" || (!!r.sum && r.kind === "heading"));
      const extra = k === 0 ? (r.indent ? 2.5 : 0) + (r.kind === "milestone" ? textMm("◇ ", 7.5) : 0) : 0;
      // 概略の見出しの行（#291）は 8pt の太字
      need = Math.max(need, textMm(t, k === 0 && r.sum && r.kind === "heading" ? 8 : 7.5) * (bold ? 1.08 : 1) + extra);
    }
    c.w = Math.round(Math.max(c.key === "name" ? o.nameMin : NAME_MIN.assignee, Math.min(c.w, need + 3)) * 10) / 10;
  });
  if (!first || first.key !== "name") return;
  let head = 0;
  for (const r of rows) {
    if (!r.span || r.kind === "memo") continue;
    const t = r.cells[0] ?? "";
    if (t) head = Math.max(head, textMm(t, 8) * 1.08 + (r.indent ? 2.5 : 0) + 3);
  }
  const short = head - cols.reduce((s, c) => s + c.w, 0);
  if (short > 0) first.w = Math.round(Math.min(cap, first.w + short) * 10) / 10;
  if (o.wide) first.w = Math.round(Math.min(cap, first.w + WIDE_NAME_MM) * 10) / 10;
}

const DOW_NAME = ["日曜", "月曜", "火曜", "水曜", "木曜", "金曜", "土曜"] as const;
/** 「2026年9月30日」 */
const jd = (ymd: string) => `${Number(ymd.slice(0, 4))}年${Number(ymd.slice(5, 7))}月${Number(ymd.slice(8, 10))}日`;
/** 「9月30日」 */
const md2 = (ymd: string) => `${Number(ymd.slice(5, 7))}月${Number(ymd.slice(8, 10))}日`;
/** 「2026年9月28日〜10月20日」（同じ年なら後ろの年は書かない） */
const spanJ = (a: string, b: string) => (a === b ? jd(a) : `${jd(a)}〜${a.slice(0, 4) === b.slice(0, 4) ? md2(b) : jd(b)}`);
const pctText = (p: number | null | undefined) => (p == null ? "" : p >= 100 ? X.done100 : `${p}%`);
const asgName = (r: Pick<ScheduleRow, "assignee">) => (!r.assignee ? "" : r.assignee.type === "text" ? r.assignee.text : (r.assignee.name ?? ""));

/** 休みの日の書き方（「日曜・祝日」）。曜日・祝日・会社の休み */
export function offDaysText(offWeekdays: readonly number[], holidayOff: boolean, companyOff: boolean): string | null {
  const parts = [...[...offWeekdays].sort((a, b) => a - b).map((w) => DOW_NAME[w]!), ...(holidayOff ? ["祝日"] : []), ...(companyOff ? ["会社の休み"] : [])];
  return parts.length ? parts.join("・") : null;
}

/**
 * 1日の幅と、期間の紙の分け方。avail＝日付に使える幅（mm）。
 *   期間が入るときは紙の幅いっぱい（F2。1日 20mm まで）。入らないときは 5mm から 2.5mm まで縮め、それでも入らなければ期間で紙を分ける（どの紙も同じ日数）
 */
export function dayLayout(nDays: number, avail: number): { dayMm: number; chunks: [number, number][] } {
  const n = Math.max(1, nDays);
  const floor2 = (v: number) => Math.floor(v * 100) / 100;
  if (n * SP_MM.dayMin <= avail + 1e-9) return { dayMm: floor2(Math.min(SP_MM.dayFill, avail / n)), chunks: [[0, n]] };
  const maxPer = Math.max(1, Math.floor(avail / SP_MM.dayMin));
  const count = Math.ceil(n / maxPer);
  const per = Math.ceil(n / count);
  const chunks: [number, number][] = [];
  for (let a = 0; a < n; a += per) chunks.push([a, Math.min(n, a + per)]);
  return { dayMm: floor2(Math.min(SP_MM.dayMax, avail / per)), chunks };
}

/** 月の日数（「2026-02-…」→ 28） */
const daysInMonth = (ymd: string): number => {
  const y = Number(ymd.slice(0, 4));
  const mo = Number(ymd.slice(5, 7));
  return mo === 2 ? (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28) : [4, 6, 9, 11].includes(mo) ? 30 : 31;
};
/** 期間の目（日・旬＝1〜10日・11〜20日・21〜末日・月＝1日はじまり）。旬・月は期間を月の1日〜末日に広げてから呼ぶ（schedulePaperModel） */
export function scaleUnits(from: string, nDays: number, scale: SchedulePaperScale): SchedulePaperUnit[] {
  const n = Math.max(1, nDays);
  const base = dayNo(from);
  const out: SchedulePaperUnit[] = [];
  let a = 0;
  for (let i = 1; i <= n; i++) {
    const d = ymdOfDayNo(base + i).slice(8, 10);
    if (i === n || scale === "day" || d === "01" || (scale === "jun" && (d === "11" || d === "21"))) {
      out.push({ a, b: i });
      a = i;
    }
  }
  return out;
}

/** 月の1日（「2026-10-18」→「2026-10-01」）と末日（→「2026-10-31」） */
export const monthStartOf = (ymd: string): string => `${ymd.slice(0, 7)}-01`;
export const monthEndOf = (ymd: string): string => `${ymd.slice(0, 8)}${String(daysInMonth(ymd)).padStart(2, "0")}`;

/**
 * 紙の目盛を決める（#291 §2.1.1）。日はそのままの期間で、旬・月は月の1日〜末日に広げた期間で数える。
 *   自動：日（日数 × 2.5mm が入る）→ 旬（旬の数 × 6mm が入る）→ 月（入らなければ月で期間を紙に分ける）
 */
export function pickScale(from: string, to: string, avail: number, want: ScheduleScale = "day"): SchedulePaperScale {
  const n = Math.max(1, dayNo(to) - dayNo(from) + 1);
  if (want === "day" || (want === "auto" && n * SP_MM.dayMin <= avail + 1e-9)) return "day";
  if (want === "jun" || want === "month") return want;
  const a = monthStartOf(from);
  const juns = scaleUnits(a, dayNo(monthEndOf(to)) - dayNo(a) + 1, "jun").length;
  return juns * SP_MM.junMin <= avail + 1e-9 ? "jun" : "month";
}

/**
 * 目盛と1日の幅と、期間の紙の分け方（#290、docs/102 §2.1・#291 §2.1.1）。avail＝日付に使える幅（mm）。
 *   自動：日（日数 × 2.5mm が入る）→ 旬（旬の数 × 6mm）→ 月（月の数 × 12mm）→ どれも入らなければ月で期間を紙に分ける（pickScale）。
 *   日は今の dayLayout のまま。旬・月は from〜 が月の1日〜末日（schedulePaperModel が広げる）で、入るなら1枚・入らなければ月の境で分ける。
 *   旬は3つとも同じ幅（紙の幅いっぱい）＝1日の幅は 旬の幅 ÷ その旬の日数。月の幅も紙の幅いっぱい（#293）・1日の幅は 月の幅 ÷ その月の日数
 *   （バー・節目・今日の線は目盛に関わらず日の位置で描く）
 */
export function scaleLayout(from: string, nDays: number, avail: number, want: ScheduleScale = "day"): { scale: SchedulePaperScale; dayMm: number; dayW: number[]; units: SchedulePaperUnit[]; chunks: [number, number][] } {
  const n = Math.max(1, nDays);
  const scale = pickScale(from, ymdOfDayNo(dayNo(from) + n - 1), avail, want);
  if (scale === "day") {
    const d = dayLayout(n, avail);
    return { scale: "day", dayMm: d.dayMm, dayW: new Array<number>(n).fill(d.dayMm), units: scaleUnits(from, n, "day"), chunks: d.chunks };
  }
  const units = scaleUnits(from, n, scale);
  // 紙は月の境で分ける（旬は1か月に3つ）
  const months = scaleUnits(from, n, "month");
  const perMonth = scale === "jun" ? 3 : 1;
  const min = scale === "jun" ? SP_MM.junMin : SP_MM.monthMin;
  const maxMonths = Math.max(1, Math.floor((avail + 1e-9) / (min * perMonth)));
  const count = Math.ceil(months.length / maxMonths);
  const per = Math.ceil(months.length / count);
  const mgroups: SchedulePaperUnit[][] = [];
  for (let k = 0; k < months.length; k += per) mgroups.push(months.slice(k, k + per));
  const chunks = mgroups.map((g) => [g[0]!.a, g[g.length - 1]!.b] as [number, number]);
  const groups = chunks.map(([a, b]) => units.filter((u) => u.a >= a && u.b <= b));
  const dayW = new Array<number>(n);
  if (scale === "jun") {
    // 旬：どの紙も同じ旬の幅（旬のいちばん多い紙が幅いっぱい）。1日の幅＝旬の幅 ÷ その旬の日数
    const most = Math.max(...groups.map((g) => g.length));
    const jw = avail / most;
    for (const u of units) for (let i = u.a; i < u.b; i++) dayW[i] = jw / (u.b - u.a);
  } else {
    // 月：紙ごとの月の数（途中からの月は端数）のいちばん多い紙で月の幅を決める（紙の幅いっぱい＝旬と同じ。#293 で 40mm の上限を外した）
    const ymd = (i: number) => ymdOfDayNo(dayNo(from) + i);
    const most = Math.max(...groups.map((g) => g.reduce((t, u) => t + (u.b - u.a) / daysInMonth(ymd(u.a)), 0)));
    const mw = avail / most;
    for (const u of units) {
      const dim = daysInMonth(ymd(u.a));
      for (let i = u.a; i < u.b; i++) dayW[i] = mw / dim;
    }
  }
  return { scale, dayMm: Math.floor(Math.min(...dayW) * 100) / 100, dayW, units, chunks };
}

/**
 * 旬・月の目盛のバー（#290）：休みで区切らず1本（「済み」は稼働日で数えた分を、休みを含む暦の日の数に直す）。
 * 日の目盛は今のまま（呼ばない）
 */
export function joinRuns(runs: readonly SchedulePaperRun[]): SchedulePaperRun[] {
  if (runs.length <= 1) return [...runs];
  const a = runs[0]!.a;
  let done = 0;
  for (const u of runs) {
    if (u.done <= 0) break;
    const len = u.b - u.a + 1;
    if (u.done < len) {
      done = u.a - a + u.done;
      break;
    }
    done = u.b - a + 1;
  }
  return [{ a, b: runs[runs.length - 1]!.b, done }];
}

/** 行の紙の分け方（高さ cap mm に入るだけ。見出しの行が一番下に来るなら次の紙へ） */
export function rowChunks(rows: readonly Pick<SchedulePaperRow, "kind" | "h" | "indent">[], cap: number): [number, number][] {
  const out: [number, number][] = [];
  let a = 0;
  while (a < rows.length) {
    let h = 0;
    let b = a;
    while (b < rows.length && (b === a || h + rows[b]!.h <= cap + 1e-9)) {
      h += rows[b]!.h;
      b++;
    }
    // 見出し（とその仲間：業者ごとの段・開いた現場）が紙の一番下で、後ろに続きがあるなら次の紙へ
    const last = rows[b - 1]!;
    const headLike = last.kind === "heading" || last.kind === "section" || (last.kind === "site" && !!rows[b]?.indent);
    if (b < rows.length && b - 1 > a && headLike) b--;
    out.push([a, b]);
    a = b;
  }
  if (!out.length) out.push([0, 0]);
  return out;
}

/** 期間の日の番号（from＝0） */
const idxOf = (from: string) => (ymd: string) => dayNo(ymd) - dayNo(from);
const idx0 = (ymd: string, from: string): number => dayNo(ymd) - dayNo(from);

/** 工程のバー（区切りは contract の workRuns＝画面と同じ。済んだ所は 日数 × 進み を前から） */
function taskBar(r: { id: string; start: string; end: string | null; progress: number | null; workOffDays?: boolean }, cal: WorkCalendar, idx: (ymd: string) => number, withDone: boolean, stopMark: (ymd: string) => "☂" | "止"): SchedulePaperBar {
  const end = r.end ?? r.start;
  const rc = calendarForRow({ kind: "task", id: r.id, workOffDays: r.workOffDays }, cal);
  const runs0 = workRuns(r.start, end, rc);
  const total = runs0.reduce((n, u) => n + dayNo(u.to) - dayNo(u.from) + 1, 0) || 1;
  let left = withDone ? (total * (r.progress ?? 0)) / 100 : 0;
  const runs = runs0.map((u) => {
    const len = dayNo(u.to) - dayNo(u.from) + 1;
    const done = Math.max(0, Math.min(len, left));
    left -= len;
    return { a: idx(u.from), b: idx(u.to), done };
  });
  // 中止：その工程を選んだ中止（バーの外でも。最初の日を止めるとバーの前になる）と、バーにかかる現場全体の中止
  const stops: SchedulePaperBar["stops"] = [];
  const own = cal.rowStops?.get(r.id);
  const seen = new Set<string>();
  for (const d of own ?? []) seen.add(d);
  for (let n = dayNo(r.start); n <= dayNo(end); n++) {
    const d = ymdOfDayNo(n);
    if (cal.stops?.has(d)) seen.add(d);
  }
  // {siteShort}の休み（#293）は中止の印を付けない（休みの日と同じ細い線と見出しの「休」）
  for (const d of [...seen].sort()) if (isRowStopDay(d, r.id, cal) && !cal.siteOff?.has(d)) stops.push({ i: idx(d), mark: stopMark(d) });
  return { id: r.id, runs, stops, lane: 0, label: "", done: withDone && (r.progress ?? 0) >= 100 };
}

const emptyRow = (id: string, kind: SchedulePaperRowKind, cells: string[], h: number = SP_MM.row): SchedulePaperRow => ({
  id,
  kind,
  cells,
  span: false,
  note: "",
  indent: false,
  bars: [],
  milestones: [],
  overlap: [],
  after: null,
  lanes: 1,
  h,
});

/** 紙の形（行・日・バー・紙の割り付け） */
export function schedulePaperModel(src: SchedulePaperSource, opts: SchedulePaperOptions): SchedulePaperModel {
  const paper = { key: opts.paper, ...SCHEDULE_PAPERS[opts.paper] };
  const narrow = paper.w < 250;
  const w = narrow ? W.narrow : W.wide;
  const inc = opts.include;
  const from = opts.from;
  const to = opts.to;
  const n = Math.max(1, dayNo(to) - dayNo(from) + 1);
  const idx = idxOf(from);
  const inPeriod = (ymd: string) => ymd >= from && ymd <= to;
  const tx = scheduleFill(src.vocab?.labels, src.vocab?.product);
  const rain = scheduleShowsRain(src.vocab?.product ?? "genba");
  // 進捗を％で入力（#292）：省けば％（前と同じ紙）。％ OFF は列「状況」（空／着手／✓）・バーは 完了＝全部・着手＝半分・未着手＝無し
  const pct = (src.kind === "group" ? src.progressPct : (src.progressPct ?? src.ov.company.progressPct)) ?? true;
  const progText = (r: { progress: number | null; actualStart?: string | null | undefined }) => (pct ? pctText(r.progress ?? 0) : stateMarkOf(r));
  const colProg = pct ? X.colProgress : X.colState;

  let title: string;
  let addressee: string | null = null;
  let meta: [string, string][] = [];
  let staff = "";
  let cols: SchedulePaperColumn[];
  let rows: SchedulePaperRow[] = [];
  let dayCal: WorkCalendar;
  let crew: number[] | null = null;
  let hasCrew = false;
  let note: string;
  const legend = new Set<SchedulePaperLegendKey>();
  /** 旬・月の目盛の帯の材料（会社の休み・現場全体の中止の期間。#290） */
  const bandSrc: { kind: SchedulePaperBand["kind"]; from: string; to: string; name: string }[] = [];

  if (src.kind === "group") {
    const s = src.schedule;
    const cal = workCalendar({ offWeekdays: s.offWeekdays, holidayOff: s.holidayOff, useCompanyOffDays: s.useCompanyOffDays }, src.holidays, {
      companyOffDays: src.companyOffDays,
      stops: s.stops,
      rowDays: s.rowDays ?? [],
      workDays: s.workDays ?? [],
    });
    dayCal = cal;
    const reason = new Map(s.stops.map((x) => [x.date, x.reason]));
    if (s.useCompanyOffDays) for (const o of src.companyOffDays) bandSrc.push({ kind: "companyOff", from: o.from, to: o.to, name: o.name });
    for (const run of dateRuns(s.stops.filter((x) => x.scope === "all" && x.reason !== "off").map((x) => x.date))) {
      const note = s.stops.find((x) => x.date === run.from)?.note;
      bandSrc.push({ kind: "stop", from: run.from, to: run.to, name: note || X.legendStop });
    }
    // {siteShort}の休み（#293）：旬・月の目盛は帯「休み」（メモがあればメモ）。会社の休みと同じ淡い地
    for (const run of dateRuns(s.stops.filter((x) => x.reason === "off").map((x) => x.date))) {
      const note = s.stops.find((x) => x.date === run.from)?.note;
      bandSrc.push({ kind: "companyOff", from: run.from, to: run.to, name: note || X.siteOffBand });
    }
    // ☂ は雨の印を出す製品だけ（営業力は協力会社の「中止」も「止」。#142 段5）
    const stopMark = (d: string): "☂" | "止" => (reason.get(d) === "other" || !rain ? "止" : "☂");
    title = tx(X.titleGroup);
    addressee = inc.addressee && src.addressee.trim() ? src.addressee.trim() : null;
    const span = scheduleSpanOf(s.rows);
    meta = [[src.labels.subject, src.subject], ...(span ? [[src.labels.period, spanJ(span.from, span.to)] as [string, string]] : [])];
    staff = src.staff;
    hasCrew = inc.crew && s.rows.some((r) => r.kind === "task" && r.crew != null);
    // 概略（#291）：見出しの行だけ・中の{task}を名前つきで並べる。右端に「備考」（見出しの説明。概略のときだけ）
    const sum = inc.summary;
    cols = [
      { key: "name", label: tx(sum ? X.colTrade : X.colName), w: w.name, align: "l" },
      ...(inc.assignee ? [{ key: "assignee", label: X.colAssignee, w: w.assignee, align: "l" } as const] : []),
      ...(inc.start ? [{ key: "start", label: X.colStart, w: w.start, align: "c" } as const] : []),
      ...(inc.days ? [{ key: "days", label: X.colDays, w: w.days, align: "c" } as const] : []),
      ...(inc.progress ? [{ key: "progress", label: colProg, w: w.progress, align: "c" } as const] : []),
      ...(hasCrew && !sum ? [{ key: "crew", label: X.colCrew, w: w.crew, align: "c" } as const] : []),
      ...(inc.amount && !sum ? [{ key: "amount", label: X.colAmount, sub: X.internal, w: w.amount, align: "r" } as const] : []),
    ];
    const noteCol: SchedulePaperColumn | null = sum && inc.note ? { key: "note", label: X.colNote, w: w.note, align: "l" } : null;
    const cellsOf = (r: ScheduleRow, v: Record<string, string>) => cols.map((c) => v[c.key] ?? "");
    /** 見出しに手で入れた期間（期間の中だけ） */
    const planOf = (r: ScheduleRow): { a: number; b: number } | null =>
      r.kind === "heading" && r.start && r.end && r.end >= from && r.start <= to ? { a: Math.max(0, idx(r.start)), b: Math.min(n - 1, idx(r.end)) } : null;
    const barOf = (r: ScheduleRow): SchedulePaperBar => ({
      ...taskBar({ id: r.id, start: r.start!, end: r.end, progress: barDonePct(r, pct), workOffDays: r.workOffDays }, cal, idx, inc.progress, stopMark),
      name: r.name,
      dashed: !!r.intermittent,
    });
    if (sum) {
      // 見出しの無い{task}・節目は1行のまま。メモは出さない
      let cur: SchedulePaperRow | null = null;
      s.rows.forEach((r, i) => {
        if (r.kind === "memo") return;
        if (r.kind === "heading") {
          const hs = headingSummary(s.rows, i, pct);
          cur = emptyRow(
            r.id,
            "heading",
            cellsOf(r, {
              name: r.name,
              assignee: asgName(r),
              start: hs.start ? mdOf(hs.start) : "",
              days: hs.start && hs.end ? String(workDaysBetween(hs.start, hs.end, cal)) : "",
              progress: !pct ? headingDoneText(s.rows, i) : hs.progress == null ? "" : pctText(hs.progress),
            }),
          );
          cur.sum = true;
          cur.note = r.note ?? "";
          cur.plan = planOf(r);
          cur.msNames = [];
          rows.push(cur);
          if (cur.plan) legend.add("plan");
          return;
        }
        const target: SchedulePaperRow | null = headingIndexOf(s.rows, i) >= 0 ? cur : null;
        const row = target ?? emptyRow(r.id, r.kind === "milestone" ? "milestone" : "task", cellsOf(r, { name: r.name, assignee: asgName(r), start: r.start ? mdOf(r.start) : "", days: r.kind === "task" && r.days != null && r.start ? String(r.days) : r.kind === "milestone" ? "—" : "", progress: r.kind === "task" ? progText(r) : "" }));
        if (!target) {
          row.sum = true;
          row.msNames = [];
          rows.push(row);
        }
        if (r.kind === "milestone") {
          if (r.start && inPeriod(r.start)) {
            row.milestones.push({ i: idx(r.start), done: inc.progress && (r.progress ?? 0) >= 100 });
            row.msNames!.push(r.name);
            legend.add("milestone");
          }
          return;
        }
        if (r.start) {
          const bar = barOf(r);
          row.bars.push(bar);
          if (bar.runs.some((u) => u.b >= 0 && u.a < n)) legend.add("plan");
          if (bar.stops.some((x) => x.i >= 0 && x.i < n)) legend.add("stop");
        }
      });
      if (noteCol) {
        cols.push(noteCol);
        for (const r of rows) r.cells.push(r.note);
      }
    }
    if (!sum) s.rows.forEach((r, i) => {
      const under = headingIndexOf(s.rows, i) >= 0;
      if (r.kind === "heading") {
        const plan = planOf(r);
        const hs = headingSummary(s.rows, i, pct);
        rows.push({ ...emptyRow(r.id, "heading", [r.name]), span: true, note: r.note ?? "", plan, planDates: plan && hs.start && hs.end ? { from: hs.start, to: hs.end } : null });
        if (plan) legend.add("plan");
        return;
      }
      if (r.kind === "memo") {
        if (!inc.memo) return;
        rows.push({ ...emptyRow(r.id, "memo", [r.name]), span: true, indent: under });
        return;
      }
      if (r.kind === "milestone") {
        const row = emptyRow(r.id, "milestone", cellsOf(r, { name: r.name, assignee: asgName(r), start: r.start ? mdOf(r.start) : "", days: "—", progress: inc.progress && (r.progress ?? 0) >= 100 ? X.done100 : "" }));
        row.indent = under;
        if (r.start && inPeriod(r.start)) {
          row.milestones.push({ i: idx(r.start), done: inc.progress && (r.progress ?? 0) >= 100 });
          legend.add("milestone");
        }
        rows.push(row);
        return;
      }
      const row = emptyRow(
        r.id,
        "task",
        cellsOf(r, {
          name: r.name,
          assignee: asgName(r),
          start: r.start ? mdOf(r.start) : "",
          days: r.days != null && r.start ? String(r.days) : "",
          progress: progText(r),
          crew: r.crew != null ? String(r.crew) : "",
          amount: r.amount != null ? yen(r.amount) : "",
        }),
      );
      row.indent = under;
      if (r.start) {
        const bar = barOf(r);
        row.bars.push(bar);
        if (bar.runs.some((u) => u.b >= 0 && u.a < n)) legend.add("plan");
        if (bar.stops.some((x) => x.i >= 0 && x.i < n)) legend.add("stop");
      }
      rows.push(row);
    });
    note = X.note(offDaysText(s.offWeekdays, s.holidayOff, s.useCompanyOffDays && src.companyOffDays.length > 0), inc.progress ? md2(src.today) : null, colProg);
  } else {
    const ov = src.ov;
    const { companyCal, rows: infos, overlaps } = overviewRowsOf(ov);
    dayCal = companyCal;
    for (const o of ov.company.offDays) bandSrc.push({ kind: "companyOff", from: o.from, to: o.to, name: o.name });
    const byRow = new Map(infos.map((x) => [x.row.id, x]));
    title = tx(src.mode === "vendor" ? X.titleVendor : X.titleSite);
    meta = [[X.period, spanJ(opts.shown?.from ?? from, opts.shown?.to ?? to)]];
    const stopReason = new Map<string, Map<string, ScheduleStop["reason"]>>(ov.schedules.map((it) => [it.scheduleId, new Map(it.stops.map((x) => [x.date, x.reason]))]));
    const markOf = (scheduleId: string) => (d: string): "☂" | "止" => (stopReason.get(scheduleId)?.get(d) === "other" || !rain ? "止" : "☂");
    const partnerOf = new Map(ov.partners.map((p) => [p.id, p]));
    const nameOf = (r: { assignee: ScheduleRow["assignee"] }) => (!r.assignee ? "" : r.assignee.type === "partner" ? (partnerOf.get(r.assignee.id)?.name ?? r.assignee.name ?? "") : asgName(r));
    const crewSrc: { dates: readonly string[]; crew: number | null }[] = [];
    if (src.mode === "site") {
      const sites = overviewSitesOf(ov, src.q);
      hasCrew = inc.crew && sites.some((it) => it.rows.some((r) => r.kind === "task" && r.crew != null));
      cols = [
        { key: "name", label: tx(X.colSite), w: w.site, align: "l" },
        { key: "assignee", label: X.colAssignee, w: w.siteAsg, align: "l" },
        ...(inc.start ? [{ key: "start", label: X.colStart, w: w.start, align: "c" } as const] : []),
        ...(inc.days ? [{ key: "days", label: X.colDays, w: w.days, align: "c" } as const] : []),
        { key: "progress", label: colProg, w: w.progress, align: "c" },
        ...(hasCrew ? [{ key: "crew", label: X.colCrew, w: w.crew, align: "c" } as const] : []),
      ];
      const open = new Set(src.open);
      // 開いた現場（#291 から{schedule}の id。前の出力の URL の案件の id も受ける）
      const isOpen = (it: { scheduleId: string; groupId: string | null }) => open.has(it.scheduleId) || (!!it.groupId && open.has(it.groupId));
      for (const it of sites) {
        const siteOverlap = it.rows.some((r) => overlaps.byRow.has(r.id));
        const row = emptyRow(it.scheduleId, "site", cols.map((c) => ({ name: it.groupName, assignee: [it.clientName, it.staffName].filter(Boolean).join("・"), start: it.start ? mdOf(it.start) : "", progress: !pct && it.taskCount != null ? (it.taskCount ? `${it.doneCount ?? 0}／${it.taskCount}` : "") : pctText(it.progress) })[c.key as "name"] ?? ""));
        if (it.start && it.end) {
          const len = dayNo(it.end) - dayNo(it.start) + 1;
          row.bars.push({ id: it.scheduleId, runs: [{ a: idx(it.start), b: idx(it.end), done: (len * (it.progress ?? 0)) / 100 }], stops: [], lane: 0, label: "", done: (it.progress ?? 0) >= 100 });
          if (idx(it.end) >= 0 && idx(it.start) < n) legend.add("plan");
          const after = [it.lateDays > 0 ? X.late(it.lateDays) : "", siteOverlap ? X.legendOverlap : ""].filter(Boolean).join("・");
          if (after) row.after = { i: Math.min(idx(it.end), n - 1), text: after };
        }
        rows.push(row);
        for (const r of it.rows) {
          if (r.kind === "task") crewSrc.push({ dates: byRow.get(r.id)?.dates ?? [], crew: r.crew });
        }
        if (!isOpen(it)) continue;
        for (const r of [...it.rows].sort((a, b) => a.position - b.position)) {
          const info = byRow.get(r.id)!;
          const t = emptyRow(
            r.id,
            r.kind,
            cols.map((c) => ({ name: r.name, assignee: nameOf(r), start: mdOf(r.start), days: r.kind === "task" && r.days != null ? String(r.days) : "—", progress: r.kind === "task" ? progText(r) : "", crew: r.crew != null ? String(r.crew) : "" })[c.key as "name"] ?? ""),
          );
          t.indent = true;
          if (r.kind === "milestone") {
            if (inPeriod(r.start)) {
              t.milestones.push({ i: idx(r.start), done: (r.progress ?? 0) >= 100 });
              legend.add("milestone");
            }
          } else {
            const bar = taskBar({ ...r, progress: barDonePct(r, pct) }, info.cal, idx, true, markOf(it.scheduleId));
            t.bars.push(bar);
            if (bar.stops.some((x) => x.i >= 0 && x.i < n)) legend.add("stop");
            t.overlap = (overlaps.byRow.get(r.id)?.dates ?? []).filter(inPeriod).map(idx);
          }
          rows.push(t);
        }
      }
    } else {
      const people = overviewPeopleOf(ov, infos, overlaps, src.q);
      cols = [
        { key: "name", label: tx(X.colPerson), w: w.person, align: "l" },
        { key: "sites", label: tx(X.colSites), w: w.sites, align: "c" },
        { key: "overlap", label: X.colOverlap, w: w.overlap, align: "c" },
      ];
      const secs: { k: "p" | "u" | "t" | "n"; label: (n: number) => string }[] = [
        { k: "p", label: () => tx("{contractor}") },
        { k: "u", label: () => "自社" },
        { k: "t", label: () => "手入力の担当" },
        { k: "n", label: (k) => tx(`担当なし ${k}{task}`) },
      ];
      for (const sec of secs) {
        const list = people.filter((p) => p.kind === sec.k);
        if (!list.length) continue;
        rows.push({ ...emptyRow(`sec-${sec.k}`, "section", [sec.label(list.reduce((k, p) => k + p.bars.length, 0))]), span: true });
        for (const p of list) {
          const lanes = lanesOf(p.bars.map((b) => ({ id: b.id, start: b.start, end: b.end })));
          const row = emptyRow(p.key, "person", [p.kind === "u" ? `${p.name}（自社）` : p.name, tx(X.sitesN(p.groups)), p.overlapDays.length ? X.daysN(p.overlapDays.length) : ""]);
          row.lanes = Math.max(1, lanes.count);
          row.h = Math.max(SP_MM.row, row.lanes * SP_MM.lane + 1.8);
          for (const b of p.bars) {
            const info = byRow.get(b.id)!;
            const bar = taskBar({ id: b.id, start: b.start, end: b.end, progress: barDonePct(b, pct), workOffDays: b.workOffDays }, info.cal, idx, true, markOf(b.scheduleId));
            bar.lane = lanes.lane.get(b.id) ?? 0;
            bar.label = b.groupName;
            row.bars.push(bar);
            if (bar.runs.some((u) => u.b >= 0 && u.a < n)) legend.add("plan");
            if (bar.stops.some((x) => x.i >= 0 && x.i < n)) legend.add("stop");
            crewSrc.push({ dates: info.dates, crew: b.crew });
          }
          row.overlap = p.overlapDays.filter(inPeriod).map(idx);
          rows.push(row);
        }
      }
    }
    if (inc.crew) {
      const byDate = crewByDate(crewSrc);
      crew = Array.from({ length: n }, (_, k) => byDate.get(ymdOfDayNo(dayNo(from) + k)) ?? 0);
    }
    note = X.note(offDaysText(ov.company.offWeekdays, ov.company.holidayOff, ov.company.offDays.length > 0), md2(src.today), colProg);
  }

  const days: SchedulePaperDay[] = Array.from({ length: n }, (_, k) => {
    const ymd = ymdOfDayNo(dayNo(from) + k);
    const hol = dayCal.holidays.has(ymd);
    // {siteShort}の休み（#293）は曜日の段に「休」（墨）
    const siteOff = !!dayCal.siteOff?.has(ymd);
    // {siteShort}の作業日（#296）は地色なし（off が false）・曜日の字は墨（hol・sun・sat を付けない）
    const siteWork = isSiteWorkDay(ymd, dayCal);
    return {
      ymd,
      d: Number(ymd.slice(8, 10)),
      w: siteOff ? X.siteOffMark : hol ? "祝" : WEEKDAY_JA[weekdayOf(ymd)]!,
      off: isOffDay(ymd, dayCal),
      hol,
      ...(siteOff ? { siteOff } : {}),
      ...(siteWork ? { siteWork } : {}),
    };
  });
  if (rows.some((r) => r.overlap.length)) legend.add("overlap");
  const todayIdx = inc.today && inPeriod(src.today) ? idx(src.today) : null;

  // 紙の割り付け（名前の列は中身に合わせて縮める）
  fitNameColumns(cols, rows, { nameMin: src.kind === "group" ? (narrow ? NAME_MIN.group.narrow : NAME_MIN.group.wide) : NAME_MIN.overview, wide: !!opts.wideName });
  const leftW = cols.reduce((s, c) => s + c.w, 0);
  const contentW = paper.w - SP_MM.margin * 2;
  const contentH = paper.h - SP_MM.margin * 2;
  const headH = src.kind === "group" ? SP_MM.headGroup : SP_MM.headOverview;
  const bodyCap = contentH - headH - SP_MM.date - SP_MM.foot - (crew ? SP_MM.crew : 0);
  // 旬・月の目盛（#291 §2.1.1）：表は月の1日〜末日（工期が 10/18 からでも 10/1 から。旬は月ごとに3つ）
  const sc = pickScale(from, to, contentW - leftW, opts.scale ?? "day");
  if (sc !== "day" && (from !== monthStartOf(from) || to !== monthEndOf(to))) {
    return schedulePaperModel(src, { ...opts, from: monthStartOf(from), to: monthEndOf(to), scale: sc, shown: opts.shown ?? { from, to } });
  }
  const lay = scaleLayout(from, n, contentW - leftW, sc);
  const { dayMm, chunks } = lay;
  // 概略の行（#291）：中の{task}を段に分ける。名前（7pt）の幅も日に直して数える＝名前どうしが重ならない
  if (rows.some((r) => r.sum)) {
    const wAt = (i: number) => lay.dayW[Math.max(0, Math.min(n - 1, i))] ?? dayMm;
    for (const r of rows) {
      if (!r.sum) continue;
      const lastEnd: number[] = [];
      const items = [
        ...r.bars.map((b) => ({ b, a: b.runs[0]?.a ?? 0, e: b.runs[b.runs.length - 1]?.b ?? 0, label: b.name ?? "" })),
      ].sort((p, q) => p.a - q.a || p.e - q.e);
      for (const it of items) {
        let e = it.e;
        let need = textMm(it.label, 7) + 1;
        for (let k = it.a; need > 0 && k < n + 400; k++) {
          need -= wAt(k);
          if (k > e) e = k;
        }
        let lane = lastEnd.findIndex((x) => x < it.a);
        if (lane < 0) lane = lastEnd.length;
        lastEnd[lane] = e;
        it.b.lane = lane;
      }
      // 節目は最後の段の下に1段（名前つき）
      r.lanes = Math.max(1, lastEnd.length + (r.milestones.length ? 1 : 0));
      r.h = Math.max(SP_MM.row, 0.8 + r.lanes * SP_MM.sumLane);
    }
  }
  const rc = rowChunks(rows, bodyCap);
  const pages: SchedulePaperPage[] = [];
  for (const d of chunks) for (const r of rc) pages.push({ rows: r, days: d });

  // 旬・月の目盛（#290）：バーは休みで区切らず1本。会社の休みと現場全体の中止は帯（期間の中だけ）
  const bands: SchedulePaperBand[] = [];
  if (lay.scale !== "day") {
    for (const r of rows) for (const b of r.bars) b.runs = joinRuns(b.runs);
    for (const x of bandSrc) {
      const a = Math.max(0, idx(x.from));
      const b = Math.min(n - 1, idx(x.to));
      if (a <= b) bands.push({ kind: x.kind, a, b, name: x.name });
    }
    bands.sort((p, q) => p.a - q.a || (p.kind === q.kind ? 0 : p.kind === "companyOff" ? -1 : 1));
  }

  const order: SchedulePaperLegendKey[] = ["done", "plan", "off", "companyOff", "stop", "milestone", "today", "overlap"];
  if (inc.progress || src.kind === "overview") legend.add("done");
  if (lay.scale === "day") {
    if (days.some((x) => x.off)) legend.add("off");
  } else {
    // 旬・月：日曜・祝日は塗らない＝「休み」の代わりに「会社の休み」「中止」
    if (bands.some((b) => b.kind === "companyOff")) legend.add("companyOff");
    if (bands.some((b) => b.kind === "stop")) legend.add("stop");
  }
  if (todayIdx != null) legend.add("today");
  return {
    kind: src.kind,
    paper,
    include: inc,
    title,
    addressee,
    meta,
    company: src.company,
    created: jd(src.today),
    staff,
    cols,
    leftW,
    from,
    to,
    days,
    dayMm,
    scale: lay.scale,
    dayW: lay.dayW,
    units: lay.units,
    bands,
    todayIdx,
    rows,
    crew,
    legend: order.filter((k) => legend.has(k)),
    note,
    pages,
    hasCrew,
    rain,
    sheetMain: tx(X.sheetMain),
    wideName: !!opts.wideName,
  };
}

/* ============================================================================
 * 紙の HTML
 * ========================================================================== */

/** 紙の書体（今の PDF と同じ Noto Sans CJK JP をサーバーで埋め込む。BIZ UDPゴシックが入っていればそちら） */
export const SP_FONT = `'BIZ UDPGothic','Noto Sans CJK JP','Noto Sans JP','Hiragino Sans','Yu Gothic',sans-serif`;
const C = { ink: "#1A1A18", ink2: "#55534C", ai: "#123C63", aiL: "#DCE6F0", shu: "#9E2718", shuL: "#F7E7E3", line: "#DCD9D1", sep: "#E3E1DA", frame: "#8C8880", off: "#ECEAE4", head: "#F3F2EE", band: "#EFEEE9" } as const;
const f2 = (v: number) => `${Math.round(v * 100) / 100}mm`;
/** 文字の幅（mm）のおおよそ：全角 1em・数字 0.66em（BIZ UDPゴシックの数字は広い）・ほかの半角と半角カナ 0.55em */
export function textMm(s: string, pt: number): number {
  const em = pt * 0.3528;
  let w = 0;
  for (const ch of s) w += /[0-9]/.test(ch) ? 0.66 * em : /[\u0000-ÿ｡-ﾟ]/.test(ch) ? 0.55 * em : em;
  return w;
}

/**
 * 線の太さ（mm）と色。線・地・バー・節目・中止・今日の線は紙ごとに1枚の SVG で描く
 * （CSS の枠線は Chromium の PDF で 1px＝約 0.26mm に丸められ、細い罫と太い枠の差が消える。SVG の線は mm の太さのまま）。
 * 表の外枠は最後に1本の四角で描く（地や見出しの行が重なっても途切れない）
 */
const LW = { day: 0.1, row: 0.12, col: 0.12, head: 0.12, sep: 0.3, frame: 0.4, bar: 0.25, gap: 0.3, ms: 0.3, stop: 0.3, ovl: 0.3, today: 0.4 } as const;
const LC = { day: "#DAD7CF", row: "#CFCCC4", col: "#CFCCC4", head: "#CFCCC4", sep: "#8C8880", frame: "#1A1A18" } as const;
const n3 = (v: number) => String(Math.round(v * 1000) / 1000);
const sRect = (x: number, y: number, w: number, h: number, attrs: string) => `<rect x="${n3(x)}" y="${n3(y)}" width="${n3(Math.max(0, w))}" height="${n3(Math.max(0, h))}" ${attrs}/>`;
const sLine = (x1: number, y1: number, x2: number, y2: number, color: string, w: number) =>
  `<path d="M${n3(x1)} ${n3(y1)}L${n3(x2)} ${n3(y2)}" stroke="${color}" stroke-width="${w}" fill="none"/>`;
/** 点線（#291 §2.1.1：日の境・旬の境・月の目盛の 11・21） */
const sDot = (x1: number, y1: number, x2: number, y2: number, color: string, w: number) =>
  `<path d="M${n3(x1)} ${n3(y1)}L${n3(x2)} ${n3(y2)}" stroke="${color}" stroke-width="${w}" stroke-dasharray="0.4 0.5" fill="none"/>`;
/** 月の境の実線（#291 ■3。罫線より濃い墨・今日の藍の線とは別） */
const LW_MONTH = 0.25;
/** 日付の数字と曜日の大きさ（1日の幅に合わせる。数字が隣とくっつかないように） */
const dayPtOf = (dm: number) => Math.min(7.5, Math.max(4.3, Math.round(dm * 1.45 * 10) / 10));

export function schedulePaperCss(m: SchedulePaperModel): string {
  const dayPt = dayPtOf(m.dayMm);
  return `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  @page { size: ${m.paper.w}mm ${m.paper.h}mm; margin: 0; }
  .sp-page { position: relative; width: ${m.paper.w}mm; height: ${m.paper.h}mm; overflow: hidden; background: #fff; color: ${C.ink}; font-family: ${SP_FONT}; font-size: 7.5pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; page-break-after: always; break-after: page; }
  .sp-page:last-child { page-break-after: auto; break-after: auto; }
  .sp-page > div { position: absolute; }
  .sp-page > svg { position: absolute; left: 0; top: 0; overflow: visible; }
  .sp-t { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sp-title { font-size: 20pt; font-weight: 700; letter-spacing: 0.5em; text-indent: 0.5em; text-align: center; border-bottom: 3px double ${C.ink}; line-height: 1.3; }
  .sp-to { font-size: 12pt; font-weight: 700; border-bottom: 0.3mm solid ${C.ink}; line-height: 1.5; }
  .sp-meta { font-size: 8.5pt; line-height: 1.5; }
  .sp-co { font-size: 10pt; font-weight: 700; text-align: right; }
  .sp-sm { font-size: 8pt; text-align: right; line-height: 1.5; color: ${C.ink}; }
  .sp-hc { font-weight: 700; text-align: center; display: flex; align-items: center; justify-content: center; line-height: 1.2; flex-direction: column; }
  .sp-hc small { font-size: 6pt; font-weight: 400; color: ${C.ink2}; }
  .sp-mo { font-weight: 700; font-size: 7pt; line-height: ${SP_MM.dateRow}mm; padding-left: 0.6mm; white-space: nowrap; }
  .sp-dd, .sp-dw { text-align: center; line-height: ${SP_MM.dateRow}mm; font-size: ${dayPt}pt; white-space: nowrap; }
  .sp-dw { color: ${C.ink2}; }
  .sp-dd.hol, .sp-dw.hol { color: ${C.ink2}; }
  .sp-dw.sat { color: ${C.ai}; } .sp-dw.sun { color: ${C.shu}; }
  .sp-dd.td, .sp-dw.td { color: ${C.ai}; font-weight: 700; }
  .sp-c { padding: 0 1mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sp-c.l { text-align: left; } .sp-c.c { text-align: center; } .sp-c.r { text-align: right; font-family: 'BIZ UDGothic','Noto Sans CJK JP','Noto Sans Mono CJK JP',monospace; }
  .sp-c.hd { font-weight: 700; font-size: 8pt; }
  .sp-c.hd i { font-style: normal; font-weight: 400; color: ${C.ink2}; font-size: 7pt; margin-left: 2mm; }
  .sp-c.memo { color: ${C.ink2}; }
  .sp-c.ms, .sp-c.site, .sp-c.person, .sp-c.sec { font-weight: 700; }
  .sp-c.sec { font-size: 8pt; }
  .sp-lbl { font-size: 6.5pt; white-space: nowrap; overflow: hidden; line-height: ${SP_MM.bar}mm; color: ${C.ink}; padding-left: 0.8mm; }
  .sp-after { font-size: 6.5pt; font-weight: 700; color: ${C.shu}; white-space: nowrap; }
  .sp-todayl { font-size: 6.5pt; font-weight: 700; color: ${C.ai}; text-align: center; width: 10mm; }
  .sp-crew { text-align: center; font-weight: 700; font-size: ${dayPt}pt; }
  .sp-leg { font-size: 7pt; display: flex; align-items: center; gap: 1.2mm; white-space: nowrap; }
  .sp-leg svg { display: block; }
  .sp-legs { display: flex; gap: 4mm; align-items: center; }
  .sp-note { font-size: 7pt; color: ${C.ink2}; white-space: nowrap; }
  .sp-pno { font-size: 7pt; color: ${C.ink2}; text-align: right; }
${m.scale === "day" ? "" : COARSE_CSS}${m.scale !== "day" || m.rows.some((r) => r.sum || r.plan) ? BARNAME_CSS : ""}${m.include.barDates ? BARDATE_CSS : ""}${m.days.some((d) => d.siteOff) ? SITEOFF_CSS : ""}${m.days.some((d) => d.siteWork) ? SITEWORK_CSS : ""}`;
}
/** {siteShort}の休みの曜日の段の「休」（#293 → #298 で朱の太字＝画面の見出しと同じ）。{siteShort}の休みのある紙だけに入れる（無い紙は前と同じ HTML） */
const SITEOFF_CSS = `  .sp-dw.so { color: ${C.shu}; font-weight: 700; }
`;
/** {siteShort}の作業日の曜日の段（墨。#296）。作業日のある紙だけに入れる（無い紙は前と同じ HTML） */
const SITEWORK_CSS = `  .sp-dw.sw { color: ${C.ink}; }
`;
/** {task}名・見出しの枠の名前・概略の備考（#291）。使う紙だけに入れる */
const BARNAME_CSS = `  .sp-blbl { font-size: 7pt; line-height: 2.6mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${C.ink}; padding-left: 0.3mm; }
  .sp-blbl.ms { font-weight: 700; }
  .sp-blbl.ms.r { text-align: right; padding-left: 0; padding-right: 0.3mm; }
  .sp-note2 { white-space: normal; display: flex; align-items: center; font-size: 6.5pt; line-height: 1.25; }
  .sp-note2 > span { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
`;
/** 旬・月の目盛の文字（#290・#291 §2.1.1）：「上旬・中旬・下旬」・帯の名前・延べ人数。日の紙には入れない */
const COARSE_CSS = `  .sp-jun { text-align: center; line-height: ${SP_MM.dateRow * 2}mm; white-space: nowrap; overflow: hidden; font-size: 7pt; }
  .sp-band { font-size: 6pt; line-height: 1.2; white-space: nowrap; color: ${C.ink2}; padding-left: 0.5mm; }
  .sp-band.stop { color: ${C.shu}; }
  .sp-crew.u { font-size: 6.5pt; }
`;
/** バーの両端の日付（#291 §2.1.1。入れるもの「バーの日付」）：墨の薄字 5.5pt */
const BARDATE_CSS = `  .sp-bd { font-size: 5.5pt; line-height: ${SP_MM.bar}mm; white-space: nowrap; color: ${C.ink2}; }
  .sp-bd.r { text-align: right; }
`;

const div = (cls: string, x: number, y: number, w: number | null, h: number | null, inner = "", extra = "") =>
  `<div class="${cls}" style="left:${f2(x)};top:${f2(y)};${w != null ? `width:${f2(w)};` : ""}${h != null ? `height:${f2(h)};` : ""}${extra}">${inner}</div>`;

const LEGEND_LABEL: Record<SchedulePaperLegendKey, string> = {
  done: X.legendDone,
  plan: X.legendPlan,
  off: X.legendOff,
  companyOff: X.legendCompanyOff,
  stop: X.legendStop,
  milestone: X.legendMilestone,
  today: X.legendToday,
  overlap: X.legendOverlap,
};
/** 凡例の見本（紙と同じ SVG の線と地）。中止の印は雨の印を出す製品だけ ☂、ほかは「止」（#142 段5） */
function legendSw(k: SchedulePaperLegendKey, rain = true, coarse = false): string {
  const svg = (w: number, h: number, body: string) => `<svg width="${w}mm" height="${h}mm" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
  // 旬・月の目盛（#290）：中止は帯（朱の淡い地と朱の細い線）
  if (coarse && k === "stop") return svg(5, 3, sRect(0, 0, 5, 3, `fill="${C.shuL}"`) + sLine(2.5, 0, 2.5, 3, C.shu, LW.stop));
  switch (k) {
    case "companyOff":
      return svg(5, 3, sRect(0, 0, 5, 3, `fill="${C.band}"`));
    case "done":
      return svg(5, 3, sRect(0.2, 0.2, 4.6, 2.6, `rx="0.5" fill="${C.ai}"`));
    case "plan":
      return svg(5, 3, sRect(0.2, 0.2, 4.6, 2.6, `rx="0.5" fill="${C.aiL}" stroke="${C.ai}" stroke-width="${LW.bar}"`));
    case "off":
      return svg(5, 3, sRect(0, 0, 5, 3, `fill="${C.off}"`));
    case "stop":
      return svg(3, 3, sRect(0.2, 0.2, 2.6, 2.6, `rx="0.4" fill="#fff" stroke="${C.shu}" stroke-width="${LW.stop}"`) + `<text x="1.5" y="1.55" font-size="2.1" text-anchor="middle" dominant-baseline="central" fill="${C.shu}">${rain ? "☂" : "止"}</text>`);
    case "milestone":
      return svg(3.2, 3.2, `<path d="M1.6 0.2L3 1.6L1.6 3L0.2 1.6Z" fill="#fff" stroke="${C.ai}" stroke-width="${LW.ms}"/>`);
    case "today":
      return svg(1, 3.2, sLine(0.5, 0, 0.5, 3.2, C.ai, LW.today));
    case "overlap":
      return svg(5, 3, sRect(0.15, 0.15, 4.7, 2.7, `fill="${C.shuL}" stroke="${C.shu}" stroke-width="${LW.ovl}"`));
  }
}

/** 1枚の紙（.sp-page の中身）：SVG（地・罫線・バー・外枠）の上に文字 */
export function schedulePageHtml(m: SchedulePaperModel, pageIndex: number): string {
  const pg = m.pages[pageIndex]!;
  const M = SP_MM.margin;
  const cw = m.paper.w - M * 2;
  const headH = m.kind === "group" ? SP_MM.headGroup : SP_MM.headOverview;
  const out: string[] = [];

  /* ---- 上の見出し ---- */
  // 題：「工程表」は字の間を広く（F2）。「工程表（業者ごと）」のように長い題は字の間を詰めて1行に
  const wide = [...m.title].length <= 4;
  const titleW = Math.max(60, Math.ceil(textMm(m.title, 20) * (wide ? 1.5 : 1.12)) + 6);
  out.push(div("sp-title", M + (cw - titleW) / 2, M, titleW, 11, esc(m.title), wide ? "" : "letter-spacing:0.12em;text-indent:0.12em;white-space:nowrap;"));
  let ly = M;
  if (m.addressee) {
    // 宛名は「御中」まで出す：入らなければ文字を小さく（12pt から 8pt まで。それでも入らなければ …）
    const aw = Math.min(95, cw / 2 - titleW / 2 - 4);
    const pt = Math.max(8, Math.min(12, Math.floor(((12 * (aw - 1)) / textMm(m.addressee, 12)) * 10) / 10));
    out.push(div("sp-to sp-t", M, ly, aw, 7, esc(m.addressee), pt < 12 ? `font-size:${pt}pt;` : ""));
    ly += 9;
  }
  for (const [k, v] of m.meta) {
    out.push(div("sp-meta sp-t", M, ly, cw / 2 - titleW / 2 - 4, 4.5, `${esc(k)}　${esc(v)}`));
    ly += 4.6;
  }
  const rx = M + cw / 2 + titleW / 2 + 4;
  const rw = M + cw - rx;
  out.push(div("sp-co sp-t", rx, M, rw, 6, esc(m.company)));
  out.push(div("sp-sm sp-t", rx, M + 6.5, rw, 4.5, `${esc(X.created)}　${esc(m.created)}`));
  if (m.staff) out.push(div("sp-sm sp-t", rx, M + 11, rw, 4.5, `${esc(X.staff)}　${esc(m.staff)}`));

  /* ---- 表の寸法 ---- */
  const [d0, d1] = pg.days;
  const nd = d1 - d0;
  const dm = m.dayMm;
  const coarse = m.scale !== "day";
  const tl = M;
  const tt = M + headH;
  const tw = m.leftW + (coarse ? m.dayW.slice(d0, d1).reduce((t, v) => t + v, 0) : nd * dm);
  const rowsOnPage = m.rows.slice(pg.rows[0], pg.rows[1]);
  const bodyH = rowsOnPage.reduce((s, r) => s + r.h, 0);
  const crewH = m.crew ? SP_MM.crew : 0;
  const th = SP_MM.date + bodyH + crewH;
  const tr = tl + tw;
  const tb = tt + th;
  const bodyTop = tt + SP_MM.date;
  const bodyBottom = bodyTop + bodyH;
  const tx = tl + m.leftW; // 日付の左端
  // 旬・月の目盛（#290）は日ごとの幅（dayW）を足して位置を出す。日の目盛は今のまま（1日の幅 × 日の番号）
  const xs: number[] = [tx];
  if (coarse) for (let i = d0; i < d1; i++) xs.push(xs[xs.length - 1]! + m.dayW[i]!);
  const dx = coarse
    ? (i: number) => (i <= d0 ? tx + (i - d0) * m.dayW[d0]! : i >= d1 ? xs[nd]! + (i - d1) * m.dayW[d1 - 1]! : xs[i - d0]!)
    : (i: number) => tx + (i - d0) * dm;
  /** その日の幅 */
  const dW = coarse ? (i: number) => m.dayW[Math.max(d0, Math.min(d1 - 1, i))]! : () => dm;
  /** 日 a〜b（両端を含む）の幅 */
  const spanW = coarse ? (a: number, b: number) => dx(b + 1) - dx(a) : (a: number, b: number) => (b - a + 1) * dm;
  /** 日の番号（端数あり）の位置 */
  const xAtF = (f: number) => dx(Math.floor(f)) + (f - Math.floor(f)) * dW(Math.floor(f));
  const clipR = coarse ? xs[nd]! : tx + nd * dm;
  const within = (a: number, b: number) => b >= d0 && a < d1;
  const todayOn = m.todayIdx != null && m.todayIdx >= d0 && m.todayIdx < d1;
  const pid = `p${pageIndex}`;

  const bg: string[] = []; // 地
  const grid: string[] = []; // 罫線
  const marks: string[] = []; // バー・中止・節目・重なりの枠（日付の枠で切る）
  const defs: string[] = [];
  const text: string[] = []; // 文字（SVG の上）

  /* 地：休みの日（日・曜日の段と本体）・今日の日の見出し・見出しの行・重なりの日 */
  if (!coarse) {
    for (let i = d0; i < d1; i++) if (m.days[i]!.off) bg.push(sRect(dx(i), tt + SP_MM.dateRow, dm, bodyBottom - tt - SP_MM.dateRow, `fill="${C.off}"`));
    if (todayOn) bg.push(sRect(dx(m.todayIdx!), tt + SP_MM.dateRow, dm, SP_MM.dateRow * 2, `fill="#E7EEF4"`));
  }
  let y = bodyTop;
  for (const r of rowsOnPage) {
    if (r.kind === "heading" || r.kind === "section") bg.push(sRect(tl, y, tw, r.h, `fill="${C.head}"`));
    for (const i of r.overlap) if (i >= d0 && i < d1) bg.push(sRect(dx(i), y, dW(i), r.h, `fill="${C.shuL}"`));
    y += r.h;
  }
  /* 旬・月の目盛の帯（#290）：会社の休みは淡い地、中止は朱の淡い地（1日の中止は細い朱の線）。名前は帯の上端に */
  const bandLines: string[] = [];
  if (coarse) {
    for (const b of m.bands) {
      if (!within(b.a, b.b)) continue;
      const a = Math.max(b.a, d0);
      const e = Math.min(b.b, d1 - 1);
      const x0 = dx(a);
      const bw = spanW(a, e);
      if (b.kind === "stop" && b.a === b.b) bandLines.push(sLine(x0 + bw / 2, bodyTop, x0 + bw / 2, bodyBottom, C.shu, LW.stop));
      else bg.push(sRect(x0, bodyTop, bw, bodyBottom - bodyTop, `fill="${b.kind === "stop" ? C.shuL : C.band}"`));
      if (b.name) text.push(div(`sp-band${b.kind === "stop" ? " stop" : ""}`, x0, bodyTop + 0.2, null, null, esc(b.name)));
    }
  }

  /* 罫線：日の縦の罫（日・曜日の段から下まで）・月の区切り（上の段）・左の表の列・行の罫・見出しの段の罫・太い区切り */
  if (coarse) {
    // 旬・月の目盛（#290・#291 §2.1.1）：旬の境（11日・21日）の縦の罫は見出しの下の段は実線・本体は点線。月の目盛は 11日・21日に本体だけ点線。
    // 月の境は上の段から本体の下まで濃い実線
    for (const u of m.units) {
      if (u.a <= d0 || u.a >= d1 || m.days[u.a]!.d === 1) continue;
      grid.push(sLine(dx(u.a), tt + SP_MM.dateRow, dx(u.a), bodyTop, LC.day, LW.day));
      grid.push(sDot(dx(u.a), bodyTop, dx(u.a), tb, LC.row, LW.day));
    }
    if (m.scale === "month") for (let i = d0 + 1; i < d1; i++) if (m.days[i]!.d === 11 || m.days[i]!.d === 21) grid.push(sDot(dx(i), bodyTop, dx(i), tb, LC.row, LW.day));
    for (let i = d0 + 1; i < d1; i++) if (m.days[i]!.d === 1) grid.push(sLine(dx(i), tt, dx(i), tb, LC.sep, LW_MONTH));
  } else {
    // 日の目盛：日・曜日の段は実線、本体は点線（#291 ■3）
    for (let i = d0 + 1; i < d1; i++) {
      grid.push(sLine(dx(i), tt + SP_MM.dateRow, dx(i), bodyTop, LC.day, LW.day));
      grid.push(sDot(dx(i), bodyTop, dx(i), tb, LC.row, LW.day));
    }
    for (let i = d0 + 1; i < d1; i++) if (m.days[i]!.d === 1) grid.push(sLine(dx(i), tt, dx(i), tt + SP_MM.dateRow, LC.head, LW.head));
  }
  let cx = tl;
  m.cols.forEach((c, k) => {
    cx += c.w;
    if (k < m.cols.length - 1) grid.push(sLine(cx, tt, cx, tb, LC.col, LW.col));
  });
  // 月の目盛の見出しは1段（#291 §2.1.1）＝段の罫は引かない
  if (m.scale !== "month") grid.push(sLine(tx, tt + SP_MM.dateRow, clipR, tt + SP_MM.dateRow, LC.head, LW.head));
  if (!coarse) grid.push(sLine(tx, tt + SP_MM.dateRow * 2, clipR, tt + SP_MM.dateRow * 2, LC.day, LW.day));
  y = bodyTop;
  for (const r of rowsOnPage.slice(0, -1)) {
    y += r.h;
    grid.push(sLine(tl, y, tr, y, LC.row, LW.row));
  }
  grid.push(sLine(tl, bodyTop, tr, bodyTop, LC.sep, LW.sep)); // 見出しと本体の境
  grid.push(sLine(tx, tt, tx, tb, LC.sep, LW.sep)); // 左の表と日付の境
  if (m.crew) grid.push(sLine(tl, bodyBottom, tr, bodyBottom, LC.sep, LW.sep)); // 本体と「その日の人数」の境

  /* 左の表の見出し（3段をまたぐ） */
  cx = tl;
  for (const c of m.cols) {
    text.push(div("sp-hc", cx, tt, c.w, SP_MM.date, `${esc(c.label)}${c.sub ? `<small>${esc(c.sub)}</small>` : ""}`));
    cx += c.w;
  }
  /* 日付の見出し：月（紙の最初の日と月の初め。年は紙の最初と1月。入らなければ「9月」だけにして、年は次の月に付ける）・日・曜日 */
  let needYear = true;
  for (let i = d0; i < d1; i++) {
    const day = m.days[i]!;
    if (i === d0 || day.d === 1) {
      const ym = day.ymd;
      let span = 0;
      for (let k = i; k < d1 && (k === i || m.days[k]!.d !== 1); k++) span++;
      if (coarse) {
        // 旬・月の目盛（#290・#291 §2.1.1）：「2026年11月」（入らなければ「11月」）。旬は上の段、月は1段（見出しの高さいっぱい）。旬は日の目盛と同じ年の決め方
        const room = spanW(i, i + span - 1) - 1;
        const long = `${Number(ym.slice(0, 4))}年${Number(ym.slice(5, 7))}月`;
        const short = `${Number(ym.slice(5, 7))}月`;
        const withYear = m.scale === "month" || needYear || ym.slice(5, 7) === "01";
        const label = withYear && textMm(long, 7) <= room ? long : textMm(short, 7) <= room ? short : "";
        if (label === long) needYear = false;
        const mh = m.scale === "month" ? SP_MM.date : SP_MM.dateRow;
        if (label) text.push(div("sp-mo", dx(i), tt, spanW(i, i + span - 1), mh, esc(label), m.scale === "month" ? `overflow:hidden;line-height:${mh}mm;` : "overflow:hidden;"));
      }
    }
    if (coarse) continue;
    if (i === d0 || day.d === 1) {
      const ym = day.ymd;
      let span = 0;
      for (let k = i; k < d1 && (k === i || m.days[k]!.d !== 1); k++) span++;
      const room = span * dm - 1;
      const withYear = needYear || ym.slice(5, 7) === "01";
      const long = `${Number(ym.slice(0, 4))}年${Number(ym.slice(5, 7))}月`;
      const short = `${Number(ym.slice(5, 7))}月`;
      const label = withYear && textMm(long, 7) <= room ? long : textMm(short, 7) <= room ? short : "";
      if (label === long) needYear = false;
      if (label) text.push(div("sp-mo", dx(i), tt, span * dm, SP_MM.dateRow, esc(label), "overflow:hidden;"));
    }
    // 曜日の色（#291 §2.1.1）：土＝青・日祝＝朱（日祝の地色は今のまま）
    const wd = weekdayOf(day.ymd);
    const cls =
      (day.siteWork ? " sw" : (day.hol ? " hol" : "") + (day.hol || wd === 0 ? " sun" : wd === 6 ? " sat" : "")) + (day.siteOff ? " so" : "") + (todayOn && i === m.todayIdx ? " td" : "");
    text.push(div(`sp-dd${cls}`, dx(i), tt + SP_MM.dateRow, dm, SP_MM.dateRow, String(day.d)));
    text.push(div(`sp-dw${cls}`, dx(i), tt + SP_MM.dateRow * 2, dm, SP_MM.dateRow, esc(day.w)));
  }
  if (m.scale === "jun") {
    // 旬の目盛（#291 §2.1.1）：下の段に「上旬・中旬・下旬」（入らなければ「上・中・下」）。旬・月の見出しに日付は出さない
    for (const u of m.units) {
      if (u.b <= d0 || u.a >= d1) continue;
      const a = Math.max(u.a, d0);
      const uw = spanW(a, Math.min(u.b, d1) - 1);
      const k = m.days[u.a]!.d === 1 ? 0 : m.days[u.a]!.d === 11 ? 1 : 2;
      const label = textMm(SCHEDULE_JUN_LABEL[k], 7) <= uw - 0.4 ? SCHEDULE_JUN_LABEL[k] : textMm(SCHEDULE_JUN_SHORT[k], 7) <= uw - 0.4 ? SCHEDULE_JUN_SHORT[k] : "";
      if (label) text.push(div("sp-jun", dx(a), tt + SP_MM.dateRow, uw, SP_MM.dateRow * 2, label));
    }
  }

  /* 行 */
  y = bodyTop;
  let clipN = 0;
  for (const r of rowsOnPage) {
    const ind = r.indent ? 2.5 : 0;
    if (r.span) {
      const cls = r.kind === "heading" ? "hd" : r.kind === "section" ? "sec" : "memo";
      const t = r.kind === "memo" ? `${esc(X.memoMark)}　${esc(r.cells[0] ?? "")}` : `${esc(r.cells[0] ?? "")}${r.note ? `<i>${esc(r.note)}</i>` : ""}`;
      text.push(div(`sp-c ${cls} l`, tl + ind, y, m.leftW - ind, r.h, t, `line-height:${f2(r.h)};`));
    } else {
      cx = tl;
      m.cols.forEach((c, k) => {
        const first = k === 0;
        const cls = `sp-c ${c.align}${first ? (r.kind === "milestone" ? " ms" : r.kind === "site" ? " site" : r.kind === "person" ? " person" : r.sum && r.kind === "heading" ? " hd" : "") : ""}`;
        const lh = r.kind === "person" ? SP_MM.row : r.h;
        const pad = first ? ind : 0;
        const t = first && r.kind === "milestone" ? `◇ ${esc(r.cells[k] ?? "")}` : esc(r.cells[k] ?? "");
        // 概略の備考（#291）：2行まで（はみ出したら …）
        if (c.key === "note") text.push(div("sp-c sp-note2 l", cx, y, c.w, lh, `<span>${t}</span>`));
        else text.push(div(cls, cx + pad, y, c.w - pad, lh, t, `line-height:${f2(lh)};`));
        cx += c.w;
      });
    }
    // 見出しに手で入れた期間（#291 ★3）：淡い藍の地に藍の細い枠
    if (r.plan && within(r.plan.a, r.plan.b)) {
      const a = Math.max(r.plan.a, d0);
      const b = Math.min(r.plan.b, d1 - 1);
      const py = r.sum ? y + 0.4 : y + (r.h - SP_MM.bar) / 2;
      const ph = r.sum ? r.h - 0.8 : SP_MM.bar;
      // 見出しの名前は左の表にある＝枠だけ（中に{task}が無ければ枠だけ）
      marks.push(sRect(dx(a), py, spanW(a, b), ph, `rx="0.4" fill="${C.aiL}" fill-opacity="0.55" stroke="${C.ai}" stroke-width="${LW.day * 1.5}"`));
      // 旬・月の目盛でバーの日付を入れるとき（#291 の残り・#293）：枠の両端に日付（中に{task}があれば中の範囲の日付）
      if (coarse && m.include.barDates && r.planDates && !r.sum) {
        const bdW = (t: string) => textMm(t, 5.5) * 1.15 + 1;
        const sl = mdOf(r.planDates.from);
        const sr = mdOf(r.planDates.to);
        const la = Math.min(a, Math.max(d0, idx0(r.planDates.from, m.from)));
        const rb = Math.max(b, Math.min(d1 - 1, idx0(r.planDates.to, m.from)));
        if (dx(la) - bdW(sl) >= tx) text.push(div("sp-bd r", dx(la) - bdW(sl), py, bdW(sl) - 0.5, ph, sl));
        if (sl !== sr && dx(rb + 1) + bdW(sr) <= clipR) text.push(div("sp-bd", dx(rb + 1) + 0.3, py, bdW(sr), ph, sr));
      }
    }
    // 重なりの日の朱の枠
    for (const i of r.overlap) if (i >= d0 && i < d1) marks.push(sRect(dx(i) + LW.ovl / 2, y + LW.ovl / 2, dW(i) - LW.ovl, r.h - LW.ovl, `fill="none" stroke="${C.shu}" stroke-width="${LW.ovl}"`));
    // 概略（#291）：段ごとに 上に{task}名・下にバー。旬・月の目盛の{task}の行：バーを下に寄せて上に{task}名
    const labelled = r.sum || (coarse && m.kind === "group" && r.kind === "task");
    const laneTop = (lane: number) =>
      r.sum
        ? y + 0.4 + lane * SP_MM.sumLane + (SP_MM.sumLane - SP_MM.bar)
        : labelled
          ? y + r.h - SP_MM.bar - 0.6
          : r.lanes > 1 || r.kind === "person"
            ? y + 0.9 + lane * SP_MM.lane + (SP_MM.lane - SP_MM.bar) / 2
            : y + (r.h - SP_MM.bar) / 2;
    const sortedBars = [...r.bars].sort((a, b) => (a.runs[0]?.a ?? 0) - (b.runs[0]?.a ?? 0));
    sortedBars.forEach((bar, bi) => {
      const top = laneTop(bar.lane);
      const bh = SP_MM.bar;
      bar.runs.forEach((u, k) => {
        const prev = bar.runs[k - 1];
        // 休みで切れた所は細い線でつなぐ
        if (prev && within(prev.b + 1, u.a - 1)) marks.push(sLine(dx(prev.b + 1), top + bh / 2, dx(u.a), top + bh / 2, C.ai, LW.gap));
        if (!within(u.a, u.b)) return;
        const x0 = dx(u.a);
        const w = spanW(u.a, u.b);
        const id = `${pid}c${clipN++}`;
        // 随時（#291）：点線の枠・地は白（済んだ所は淡い藍）
        marks.push(sRect(x0, top, w, bh, `rx="0.5" fill="${bar.dashed ? "#fff" : C.aiL}"`));
        if (u.done > 0) {
          defs.push(`<clipPath id="${id}">${sRect(x0, top, w, bh, `rx="0.5"`)}</clipPath>`);
          marks.push(sRect(x0, top, coarse ? xAtF(u.a + u.done) - x0 : u.done * dm, bh, `fill="${bar.dashed ? C.aiL : C.ai}" clip-path="url(#${id})"`));
        }
        marks.push(sRect(x0, top, w, bh, `rx="0.5" fill="none" stroke="${C.ai}" stroke-width="${LW.bar}"${bar.dashed ? ` stroke-dasharray="0.8 0.6"` : ""}`));
      });
      for (const s of bar.stops) {
        if (s.i < d0 || s.i >= d1) continue;
        // 旬・月の目盛（#290）：止めた日はバーを貫く細い朱の線（1日の幅が狭く ☂ の枠が入らない）
        if (coarse) {
          const cx = dx(s.i) + dW(s.i) / 2;
          marks.push(sLine(cx, top - 0.5, cx, top + bh + 0.5, C.shu, LW.stop));
          continue;
        }
        const sx = dx(s.i) + 0.25;
        const sw = dm - 0.5;
        marks.push(sRect(sx, top - 0.3, sw, bh + 0.6, `rx="0.4" fill="#fff" stroke="${C.shu}" stroke-width="${LW.stop}"`));
        const fs = Math.min(2.4, dm * 0.62);
        marks.push(`<text x="${n3(sx + sw / 2)}" y="${n3(top + bh / 2)}" font-size="${n3(fs)}" text-anchor="middle" dominant-baseline="central" fill="${C.shu}" font-family="${SP_FONT.replace(/"/g, "'")}">${esc(s.mark)}</text>`);
      }
      // {task}名（#291）：旬・月の目盛と概略ではバーの上に 7pt。概略は段を名前の幅で分けてある＝全部、ほかは バーの幅まで（…）
      if (labelled && bar.name) {
        const first = bar.runs[0];
        const last = bar.runs[bar.runs.length - 1];
        if (first && last && within(first.a, last.b)) {
          const x0 = Math.max(tx, dx(first.a));
          const x1 = Math.min(clipR, dx(last.b + 1));
          const lw = r.sum ? Math.min(clipR - x0, Math.max(x1 - x0, textMm(bar.name, 7) + 1)) : x1 - x0;
          if (lw >= 2) text.push(div("sp-blbl", x0, top - 2.6, lw, 2.6, esc(bar.name)));
        }
      }
      // バーの両端の日付（#291 §2.1.1）：始まりの日の左・終わりの日の右に「10/13」「11/14」。
      // 左は同じ段の前のバーにかかれば出さない、右は同じ段の次のバー（とその左の日付）・遅れの字にぶつかれば出さない。
      // 1日だけのバーは左の1つだけ（同じ日を2つ並べない。左を出せないときは右）
      if (m.include.barDates) {
        const first = bar.runs[0];
        const last = bar.runs[bar.runs.length - 1];
        if (first && last) {
          const dateAt = (i: number) => mdOf(ymdOfDayNo(dayNo(m.from) + i));
          // 字幅は少し広めに見積もる（数字と「/」は見積もりより広く描かれる＝左の日付がバーに重ならない）
          const bdW = (t: string) => textMm(t, 5.5) * 1.15 + 1;
          const prev = sortedBars.slice(0, bi).reverse().find((o) => o.lane === bar.lane);
          const prevX = prev?.runs.length ? dx(prev.runs[prev.runs.length - 1]!.b + 1) : tx;
          const sl = dateAt(first.a);
          const leftShown = first.a >= d0 && first.a < d1 && dx(first.a) - bdW(sl) >= Math.max(tx, prevX);
          if (leftShown) text.push(div("sp-bd r", dx(first.a) - bdW(sl), top, bdW(sl) - 0.5, bh, sl));
          const next = sortedBars.slice(bi + 1).find((o) => o.lane === bar.lane);
          const nextA = next?.runs[0]?.a;
          const nextX = nextA == null ? clipR : dx(nextA) - (nextA >= d0 ? bdW(dateAt(nextA)) : 0);
          const sr = dateAt(last.b);
          const afterHere = !!r.after && r.after.i === last.b && !r.sum;
          const oneDay = first.a === last.b && leftShown;
          if (!oneDay && last.b >= d0 && last.b < d1 && !afterHere && dx(last.b + 1) + bdW(sr) <= Math.min(clipR, nextX)) text.push(div("sp-bd", dx(last.b + 1) + 0.3, top, bdW(sr), bh, sr));
        }
      }
      // 業者ごと：現場名（バーに入れば中、入らなければ右。同じ段の次のバーにかかるなら出さない）
      if (bar.label) {
        const first = bar.runs[0];
        const last = bar.runs[bar.runs.length - 1];
        if (first && last && within(first.a, last.b)) {
          const x0 = Math.max(tx, dx(first.a));
          const x1 = Math.min(clipR, dx(last.b + 1));
          const tw2 = textMm(bar.label, 6.5) + 1.6;
          const next = sortedBars.slice(bi + 1).find((o) => o.lane === bar.lane);
          const nextX = next?.runs[0] ? dx(next.runs[0].a) : clipR;
          if (tw2 <= x1 - x0) text.push(div("sp-lbl", x0, top, x1 - x0, bh, esc(bar.label), bar.done ? "color:#fff;" : ""));
          else if (x1 + 1 + tw2 <= Math.min(clipR, nextX - 0.8)) text.push(div("sp-lbl", x1 + 0.6, top, tw2, bh, esc(bar.label)));
        }
      }
    });
    r.milestones.forEach((ms, mi) => {
      if (ms.i < d0 || ms.i >= d1) return;
      // 概略（#291）：節目は最後の段に ◇ と名前
      const my = r.sum ? y + 0.4 + (r.lanes - 1) * SP_MM.sumLane + SP_MM.sumLane / 2 + 0.8 : y + r.h / 2;
      const hd = coarse ? 1.4 : Math.min(1.6, dm * 0.42);
      // 旬・月の目盛は1日が狭く ◇ が日の幅より大きい＝紙の最初・最後の日でも枠の内側に（#290）
      const mx = coarse ? Math.max(tx + hd + LW.ms, Math.min(clipR - hd - LW.ms, dx(ms.i) + dW(ms.i) / 2)) : dx(ms.i) + dm / 2;
      marks.push(`<path d="M${n3(mx)} ${n3(my - hd)}L${n3(mx + hd)} ${n3(my)}L${n3(mx)} ${n3(my + hd)}L${n3(mx - hd)} ${n3(my)}Z" fill="${ms.done ? C.ai : "#fff"}" stroke="${C.ai}" stroke-width="${LW.ms}"/>`);
      const nm = r.sum ? r.msNames?.[mi] : undefined;
      if (nm) {
        // 右に入らなければ ◇ の左に（表の外にはみ出さない）
        const tw2 = textMm(nm, 7) + 1;
        if (mx + hd + 0.6 + tw2 <= clipR) text.push(div("sp-blbl ms", mx + hd + 0.6, my - 1.3, tw2, 2.6, esc(nm)));
        else text.push(div("sp-blbl ms r", Math.max(tx, mx - hd - 0.6 - tw2), my - 1.3, Math.min(tw2, mx - hd - 0.6 - tx), 2.6, esc(nm)));
      }
    });
    if (r.after && r.after.i >= d0 && r.after.i < d1) {
      const ax = dx(r.after.i + 1) + 1;
      const aw = textMm(r.after.text, 6.5) + 1;
      if (ax + aw <= clipR) text.push(div("sp-after", ax, y, aw, r.h, esc(r.after.text), `line-height:${f2(r.h)};`));
    }
    y += r.h;
  }
  // その日の人数
  if (m.crew && coarse) {
    // 旬・月の目盛（#290）：目ごとの延べの人数
    text.push(div("sp-c sec l", tl, bodyBottom, m.leftW, crewH, esc(X.crewSum), `line-height:${f2(crewH)};`));
    for (const u of m.units) {
      if (u.b <= d0 || u.a >= d1) continue;
      const a = Math.max(u.a, d0);
      const e = Math.min(u.b, d1);
      let v = 0;
      for (let i = a; i < e; i++) v += m.crew[i] ?? 0;
      if (v && textMm(String(v), 6.5) <= spanW(a, e - 1)) text.push(div("sp-crew u", dx(a), bodyBottom, spanW(a, e - 1), crewH, String(v), `line-height:${f2(crewH)};`));
    }
  } else if (m.crew) {
    text.push(div("sp-c sec l", tl, bodyBottom, m.leftW, crewH, esc(X.crewDay), `line-height:${f2(crewH)};`));
    for (let i = d0; i < d1; i++) {
      const v = m.crew[i] ?? 0;
      if (v) text.push(div("sp-crew", dx(i), bodyBottom, dm, crewH, String(v), `line-height:${f2(crewH)};`));
    }
  }
  // 今日の線（本体だけ。見出しの数字を貫かない。見出しの今日の日は藍の字と淡い地）
  const todayLine: string[] = [];
  if (todayOn) {
    const xx = dx(m.todayIdx!) + dW(m.todayIdx!) / 2;
    todayLine.push(sLine(xx, bodyTop, xx, bodyBottom, C.ai, LW.today));
    text.push(div("sp-todayl", xx - 5, tb + 0.5, 10, 3, esc(X.today)));
  }

  // SVG：地 → 罫線 → バーほか（日付の枠で切る）→ 今日の線 → 外枠（最後。途切れない）
  const W = m.paper.w;
  const H = m.paper.h;
  defs.push(`<clipPath id="${pid}tl">${sRect(tx, tt, clipR - tx, th, "")}</clipPath>`);
  out.unshift(
    `<svg width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
      `<defs>${defs.join("")}</defs>` +
      bg.join("") +
      grid.join("") +
      `<g clip-path="url(#${pid}tl)">${bandLines.join("")}${marks.join("")}${todayLine.join("")}</g>` +
      sRect(tl, tt, tw, th, `fill="none" stroke="${LC.frame}" stroke-width="${LW.frame}"`) +
      `</svg>`,
  );
  out.push(...text);

  /* ---- 下：凡例・注記・ページ ---- */
  const fy = tb + 4;
  const legend = m.legend.map((k) => `<span class="sp-leg">${legendSw(k, m.rain, coarse)}${esc(LEGEND_LABEL[k])}</span>`).join("");
  out.push(div("sp-legs", M, fy, cw - 20, 4, legend));
  out.push(div("sp-note sp-t", M, fy + 4.6, cw - 20, 4, esc(m.note)));
  out.push(div("sp-pno", M + cw - 20, fy + 4.6, 20, 4, esc(X.page(pageIndex + 1, m.pages.length))));
  return out.join("");
}

/** 紙の HTML（PDF・見本）。pages を渡すとそのページだけ（見本は1枚目だけ） */
export function renderSchedulePaper(m: SchedulePaperModel, opts: { pages?: readonly number[] } = {}): { html: string; css: string; pages: string[]; count: number } {
  const want = opts.pages ?? m.pages.map((_, i) => i);
  const pages = want.filter((i) => i >= 0 && i < m.pages.length).map((i) => schedulePageHtml(m, i));
  const css = schedulePaperCss(m);
  const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${esc(m.title)}</title><style>${css}</style></head><body>${pages.map((p) => `<div class="sp-page">${p}</div>`).join("")}</body></html>`;
  return { html, css, pages, count: m.pages.length };
}

/* ============================================================================
 * データの表（CSV と Excel の2枚目「データ」。docs/39 §7.2 の8）
 * ========================================================================== */

/**
 * 案件の工程表のデータ：種類／見出し／名前／担当／開始／終わり／日数／進み／状況／着手日／完了日（#292）／人数／休日も／雨でも／前の工程／中止の日／休みの日／作業の日／
 * {siteShort}の作業日（#296）／日のメモ（#299）／メモ（／金額）。日付の見出しのメモ（{siteShort}のメモ）があれば、見出しのすぐ下に種類「{siteShort}のメモ」の行を1つ
 */
export function scheduleDataRows(src: ScheduleGroupSource, inc: Pick<ScheduleInclude, "memo" | "amount">): string[][] {
  const s = src.schedule;
  const cal = workCalendar({ offWeekdays: s.offWeekdays, holidayOff: s.holidayOff, useCompanyOffDays: s.useCompanyOffDays }, src.holidays, {
    companyOffDays: src.companyOffDays,
    stops: s.stops,
    rowDays: s.rowDays ?? [],
    workDays: s.workDays ?? [],
  });
  const nameOf = new Map(s.rows.map((r) => [r.id, r.name]));
  const preds = new Map<string, string[]>();
  for (const l of s.links) preds.set(l.to, [...(preds.get(l.to) ?? []), nameOf.get(l.from) || "（名前なし）"]);
  const tx = scheduleFill(src.vocab?.labels, src.vocab?.product);
  // 「雨でも」の列は雨の印を出す製品だけ（営業力は列ごと外す。#142 段5 No.127）
  const rain = scheduleShowsRain(src.vocab?.product ?? "genba");
  const head: string[] = [...scheduleCsvHead(tx, rain), ...(inc.amount ? ["金額（税抜）"] : [])];
  const out: string[][] = [head];
  // 日のメモ（#299）：「11/14 資材搬入 10時」を「・」でつなぐ（日付の順）。{task}を動かしても日付に残る＝範囲の外のメモも出す
  const notes = dayNotesByRow(s.dayNotes ?? []);
  const notesText = (k: string) => [...(notes.get(k) ?? new Map<string, string>())].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([d, t]) => `${mdOf(d)} ${t}`).join("・");
  const cols = head.length;
  const noteCol = scheduleCsvHead((x) => x, true).indexOf("日のメモ");
  if (notes.has("")) {
    const line = Array.from({ length: cols }, () => "");
    line[0] = tx(X.dayNotesRow);
    line[rain ? noteCol : noteCol - 1] = notesText("");
    out.push(line);
  }
  s.rows.forEach((r, i) => {
    if (r.kind === "memo" && !inc.memo) return;
    const h = headingIndexOf(s.rows, i);
    const task = r.kind === "task";
    const stops: string[] = [];
    // {task}の日ごとの休み・作業（#293）：開始〜終わりの中の指定だけ（範囲の外の指定は効かない）
    const offs: string[] = [];
    const works: string[] = [];
    // {siteShort}の作業日（#296）：開始〜終わりの中の効いている日（休みの日・中止でない）。「休日も作業」の{task}には効かないので出さない
    const siteWorks: string[] = [];
    if (task && r.start) {
      const end = r.end ?? r.start;
      const own = cal.rowStops?.get(r.id);
      const set = new Set<string>(own ?? []);
      for (let n = dayNo(r.start); n <= dayNo(end); n++) if (cal.stops?.has(ymdOfDayNo(n))) set.add(ymdOfDayNo(n));
      // {siteShort}の休みは「中止の日」に「（休み）」付きで
      stops.push(...[...set].sort().map((d) => (cal.siteOff?.has(d) ? `${mdOf(d)}${X.siteOffSuffix}` : mdOf(d))));
      for (const [d, work] of [...(cal.rowDays?.get(r.id) ?? new Map<string, boolean>())].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
        if (d < r.start || d > end) continue;
        (work ? works : offs).push(mdOf(d));
      }
      if (!r.workOffDays) for (const d of [...(cal.siteWork ?? [])].sort()) if (d >= r.start && d <= end && isSiteWorkDay(d, cal)) siteWorks.push(mdOf(d));
    }
    const row = [
      tx(X.kindLabel[r.kind]),
      h >= 0 ? s.rows[h]!.name : "",
      r.kind === "memo" ? "" : r.name,
      r.kind === "heading" || r.kind === "memo" ? "" : asgName(r),
      r.start ?? "",
      r.kind === "task" || r.kind === "milestone" ? (r.end ?? r.start ?? "") : "",
      task && r.days != null && r.start ? String(r.days) : "",
      task || r.kind === "milestone" ? String(r.progress ?? 0) : "",
      // 状況・着手日・完了日（#292。{task}だけ。設定に関わらず入れる）
      task ? TASK_STATE_LABEL[taskStateOf(r)] : "",
      task ? (r.actualStart ?? "") : "",
      task ? (r.actualEnd ?? "") : "",
      task && r.crew != null ? String(r.crew) : "",
      task && r.workOffDays ? X.yes : "",
      task && r.rainOk ? X.yes : "",
      (preds.get(r.id) ?? []).join("・"),
      stops.join("・"),
      offs.join("・"),
      works.join("・"),
      siteWorks.join("・"),
      task ? notesText(r.id) : "",
      r.kind === "heading" ? (r.note ?? "") : r.kind === "memo" ? r.name : "",
      ...(inc.amount ? [task && r.amount != null ? String(r.amount) : ""] : []),
    ];
    out.push(rain ? row : row.filter((_c, k) => k !== SCHEDULE_CSV_RAIN_COL));
  });
  return out;
}

/** 全体の工程表のデータ（探すで絞った現場・業者。現場ごとは工程と節目、業者ごとは工程） */
export function overviewDataRows(src: ScheduleOverviewSource, customerLabel: string): string[][] {
  const ov = src.ov;
  const { rows: infos, overlaps } = overviewRowsOf(ov);
  const byRow = new Map(infos.map((x) => [x.row.id, x]));
  const partnerOf = new Map(ov.partners.map((p) => [p.id, p]));
  const asg = (r: Pick<ScheduleRow, "assignee">) => (!r.assignee ? "" : r.assignee.type === "partner" ? (partnerOf.get(r.assignee.id)?.name ?? r.assignee.name ?? "") : asgName(r));
  const tx = scheduleFill(src.vocab?.labels, src.vocab?.product);
  const out: string[][] = [overviewCsvHead(customerLabel).map(tx)];
  const line = (it: ScheduleOverview["schedules"][number], r: ScheduleOverview["schedules"][number]["rows"][number]) => [
    it.groupName,
    it.clientName,
    asg(r),
    tx(X.kindLabel[r.kind]),
    r.name,
    r.start,
    r.end ?? r.start,
    r.kind === "task" && r.days != null ? String(r.days) : "",
    String(r.progress ?? 0),
    r.kind === "task" ? TASK_STATE_LABEL[taskStateOf(r)] : "",
    r.kind === "task" ? (r.actualStart ?? "") : "",
    r.kind === "task" ? (r.actualEnd ?? "") : "",
    r.crew != null ? String(r.crew) : "",
    (overlaps.byRow.get(r.id)?.dates ?? []).map(mdOf).join("・"),
  ];
  if (src.mode === "site") {
    for (const it of overviewSitesOf(ov, src.q)) for (const r of [...it.rows].sort((a, b) => a.position - b.position)) out.push(line(it, r));
  } else {
    for (const p of overviewPeopleOf(ov, infos, overlaps, src.q)) for (const b of p.bars) {
      const info = byRow.get(b.id);
      if (info) out.push(line(info.item, info.row));
    }
  }
  return out;
}
